/**
 * API service for making authenticated requests to the backend
 */

import { getApiUrl } from './configService';

/**
 * Clear local auth state and redirect to login, preserving the current page
 * so the user lands back here after re-authenticating.
 */
const redirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('rememberMe');
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?redirect=${redirect}`;
};

/**
 * Make a simple GET request without authentication
 * @param {String} url - URL to fetch
 * @param {Object} params - Query parameters
 * @param {String} responseType - Response type (json or blob)
 * @param {Object} options - Additional options
 * @returns {Promise<Object|Blob>} - Response data
 * @throws {Error} - If the request fails
 */
export const get = async (url, params = null, responseType = 'json', options = {}) => {
  try {
    // Add query parameters if provided
    let finalUrl = url;
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      finalUrl = `${url}${queryString ? `?${queryString}` : ''}`;
    }
    
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = finalUrl.startsWith('http') ? finalUrl : finalUrl.startsWith('/') ? `${baseUrl}${finalUrl}` : `${baseUrl}/${finalUrl}`;
    
    // Generate cache key
    const cacheKey = `${fullUrl}:${responseType}`;
    
    // Check if we should use cache and if we have a cached response
    const useCache = options && options.useCache !== false; // Default to true
    const cacheTTL = (options && options.cacheTTL) || 30; // Default to 30 seconds
    
    if (useCache && responseType === 'json') {
      const cachedData = apiCache.get(cacheKey);
      if (cachedData) {
        console.log('Using cached response for:', fullUrl);
        return cachedData;
      }
    }
    
    console.log('Making GET request to:', fullUrl);

    // Include auth token if available — allows protected routes to work when logged in
    const tokenForGet = localStorage.getItem('token');
    const getHeaders = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...(tokenForGet ? { 'Authorization': `Bearer ${tokenForGet}` } : {})
    };

    // Make the request
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: getHeaders
    });
    
    console.log('GET response status:', response.status, 'for URL:', url);
    
    // Check if response is ok
    if (!response.ok) {
      // Check if this is an expected 404 for test plan endpoints (these are normal when test plans don't exist)
      const isTestPlanEndpoint = (url.includes('/api/test-plans/work-item/') &&
                                  (url.includes('/test-plan') || url.includes('/logs') || url.includes('/versions') || url.includes('/compare'))) ||
                                 url.includes('/api/test-plans/tickets/');
      const isExpected404 = response.status === 404 && isTestPlanEndpoint;

      // Only log errors for non-expected 404s
      if (!isExpected404) {
        console.error('GET response not OK:', response.status, response.statusText, 'for URL:', url);

        // 401 with a token means the session expired — redirect to login
        if (response.status === 401 && localStorage.getItem('token')) {
          redirectToLogin();
          throw new Error('Session expired. Please log in again.');
        }
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();

        // Only log error data for non-expected 404s
        if (!isExpected404) {
          console.error('Error data from GET request:', errorData);
        }

        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        // Only log JSON parsing errors for non-expected 404s
        if (!isExpected404) {
          console.error('Error parsing error response JSON:', jsonError);
        }
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response based on responseType
    let data;
    if (responseType === 'blob') {
      data = await response.blob();
    } else if (responseType === 'text') {
      data = await response.text();
    } else {
      data = await response.json();
      
      // Cache the response if caching is enabled
      if (useCache && responseType === 'json') {
        apiCache.set(cacheKey, data, cacheTTL);
      }
    }
    console.log('GET request successful for URL:', url);
    
    return data;
  } catch (error) {
    // Check if this is an expected 404 for test plan endpoints
    const isTestPlanEndpoint = (url.includes('/api/test-plans/work-item/') &&
                                (url.includes('/test-plan') || url.includes('/logs') || url.includes('/versions') || url.includes('/compare'))) ||
                               url.includes('/api/test-plans/tickets/');
    const isExpected404 = error.message && error.message.includes('404') && isTestPlanEndpoint;

    // Only log errors for non-expected 404s
    if (!isExpected404) {
      console.error('Error in get:', error, 'for URL:', url);
    }
    throw error;
  }
};

// Default API URL to use until the configuration is loaded
let BASE_URL = process.env.NODE_ENV === 'development' ? '' : 'http://localhost:5001';

// Initialize the API URL
(async () => {
  try {
    BASE_URL = await getApiUrl();
    console.log('API Service initialized with URL:', BASE_URL);
  } catch (error) {
    console.error('Failed to initialize API URL:', error);
    console.log('Using default API URL:', BASE_URL);
  }
})();

// Helper function to get the current API URL
// This is needed because the BASE_URL might be updated after initialization
const getCurrentBaseUrl = async () => {
  // In development with proxy, use empty string to make relative URLs
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.API_URL) {
    return window.RUNTIME_CONFIG.API_URL;
  }
  return BASE_URL;
};

// Simple in-memory cache for GET requests
const apiCache = {
  cache: new Map(),
  get: function(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    // Check if the cache item has expired
    if (item.expiry < Date.now()) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  },
  set: function(key, data, ttlSeconds = 30) { // Default TTL: 30 seconds
    const expiry = Date.now() + (ttlSeconds * 1000);
    this.cache.set(key, { data, expiry });
  },
  invalidate: function(urlPattern) {
    // Remove all cache entries that match the URL pattern
    for (const key of this.cache.keys()) {
      if (key.includes(urlPattern)) {
        this.cache.delete(key);
      }
    }
  }
};

/**
 * Make an authenticated GET request
 * @param {String} url - URL to fetch
 * @param {Object} params - Query parameters
 * @param {String} responseType - Response type (json or blob)
 * @param {Object} options - Additional options
 * @returns {Promise<Object|Blob>} - Response data
 * @throws {Error} - If the request fails
 */
export const authGet = async (url, params = null, responseType = 'json', options = {}) => {
  // Declare timeoutId outside try block so it's accessible in finally block
  let timeoutId = null;
  
  try {
    // Add query parameters if provided
    let finalUrl = url;
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      finalUrl = `${url}${queryString ? `?${queryString}` : ''}`;
    }
    
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = finalUrl.startsWith('http') ? finalUrl : finalUrl.startsWith('/') ? `${baseUrl}${finalUrl}` : `${baseUrl}/${finalUrl}`;
    
    // Generate cache key
    const cacheKey = `${fullUrl}:${responseType}`;
    
    // Check if we should use cache and if we have a cached response
    const useCache = options && options.useCache !== false; // Default to true
    const cacheTTL = (options && options.cacheTTL) || 30; // Default to 30 seconds
    
    if (useCache && responseType === 'json') {
      const cachedData = apiCache.get(cacheKey);
      if (cachedData) {
        console.log('Using cached response for:', fullUrl);
        return cachedData;
      }
    }
    
    console.log('Making authenticated GET request to:', fullUrl);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for GET request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Check if this might be a long-running operation (based on timeout or options)
    // No default timeout - only apply timeout if explicitly set in options
    // This allows long-running operations to complete without arbitrary timeouts
    const timeout = options.timeout; // Only use timeout if explicitly provided
    const isLongRunning = timeout && timeout > 120000; // > 2 minutes
    
    // Create AbortController for timeout support (only if timeout is specified)
    let controller = null;
    if (timeout) {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        controller.abort();
        if (timeoutId) clearTimeout(timeoutId);
      }, timeout);
    }
    
    // Make the request
    console.log('Sending GET request with auth token');
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      signal: controller?.signal
    });
    
    console.log('GET response status:', response.status, 'for URL:', url);
    
    // Check for long-running operation headers
    const isLongRunningOp = response.headers.get('X-Long-Running') === 'true' || 
                            response.headers.get('X-Operation-Type');
    
    if (isLongRunningOp || isLongRunning) {
      // Import keep-alive service dynamically to avoid circular dependencies
      const keepAliveService = (await import('./keepAliveService')).default;
      keepAliveService.start({ intervalMs: 30000 });
      console.log('[API Service] Long-running operation detected, started keep-alive service');
    }
    
    // Clean up timeout if request completed
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Check if response is ok
    if (!response.ok) {
      console.error('GET response not OK:', response.status, response.statusText, 'for URL:', url);

      if (response.status === 401) {
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from GET request:', errorData);

        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response based on responseType
    let data;
    if (responseType === 'blob') {
      data = await response.blob();
    } else if (responseType === 'text') {
      data = await response.text();
    } else {
      data = await response.json();
      
      // Cache the response if caching is enabled
      if (useCache && responseType === 'json') {
        apiCache.set(cacheKey, data, cacheTTL);
      }
    }
    
    // Stop keep-alive if operation completed successfully
    if (isLongRunningOp || isLongRunning) {
      const keepAliveService = (await import('./keepAliveService')).default;
      keepAliveService.stop();
      console.log('[API Service] Long-running operation completed, stopped keep-alive service');
    }
    
    console.log('GET request successful for URL:', url);
    
    return data;
  } catch (error) {
    console.error('Error in authGet:', error, 'for URL:', url);
    
    // Stop keep-alive on error
    try {
      const keepAliveService = (await import('./keepAliveService')).default;
      if (keepAliveService.getActive()) {
        keepAliveService.stop();
        console.log('[API Service] Stopped keep-alive service due to error');
      }
    } catch (keepAliveError) {
      // Ignore errors when stopping keep-alive
    }
    
    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in GET request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  } finally {
    // Clean up timeout if still active
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

/**
 * Invalidate cache entries matching a URL pattern
 * @param {String} urlPattern - URL pattern to match
 */
export const invalidateCache = (urlPattern) => {
  console.log('Invalidating cache for URL pattern:', urlPattern);
  apiCache.invalidate(urlPattern);
};

/**
 * Make an authenticated POST request
 * @param {String} url - URL to fetch
 * @param {Object} data - Data to send
 * @param {Object} options - Additional options (timeout in milliseconds, invalidateCache)
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const authPost = async (url, data, options = {}) => {
  let timeoutId;
  // Declare timeout variables outside try block so they're accessible in catch block
  // No default timeout - only apply timeout if explicitly set in options
  // This allows long-running operations to complete without arbitrary timeouts
  const timeout = options.timeout; // Only use timeout if explicitly provided
  const isLongRunning = timeout && timeout > 120000; // > 2 minutes
  
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();

    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;

    console.log('Making authenticated POST request to:', fullUrl);
    console.log('POST request data:', data);

    // Get token from localStorage
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('No authentication token found for POST request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Create AbortController for timeout support (only if timeout is specified)
    let controller = null;
    if (timeout) {
      const { controller: ctrl, timeoutId: tid } = createAbortController(timeout);
      controller = ctrl;
      timeoutId = tid;
    }

    // Make the request
    console.log('Sending POST request with auth token');
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
      signal: controller?.signal
    });

    console.log('POST response status:', response.status, 'for URL:', url);
    
    // Check for long-running operation headers
    const isLongRunningOp = response.headers.get('X-Long-Running') === 'true' || 
                            response.headers.get('X-Operation-Type');
    
    if (isLongRunningOp || isLongRunning) {
      // Import keep-alive service dynamically to avoid circular dependencies
      const keepAliveService = (await import('./keepAliveService')).default;
      keepAliveService.start({ intervalMs: 30000 });
      console.log('[API Service] Long-running operation detected, started keep-alive service');
    }

    // Check if response is ok
    if (!response.ok) {
      console.error('POST response not OK:', response.status, response.statusText, 'for URL:', url);

      if (response.status === 401) {
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from POST request:', errorData);

        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }

        const apiError = new Error(errorData.message || `Request failed with status: ${response.status}`);
        apiError.details = errorData;
        throw apiError;
      } catch (jsonError) {
        if (jsonError instanceof Error) {
          if (jsonError.details !== undefined) throw jsonError;
          if (jsonError.message === 'Your session has timed out. Please log in again.') throw jsonError;
        }
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }

    // Parse response
    const responseData = await response.json();
    console.log('POST request successful for URL:', url);
    
    // Stop keep-alive if operation completed successfully
    if (isLongRunningOp || isLongRunning) {
      const keepAliveService = (await import('./keepAliveService')).default;
      keepAliveService.stop();
      console.log('[API Service] Long-running operation completed, stopped keep-alive service');
    }

    // Invalidate related cache entries if specified
    if (options.invalidateCache) {
      if (Array.isArray(options.invalidateCache)) {
        options.invalidateCache.forEach(pattern => invalidateCache(pattern));
      } else {
        invalidateCache(options.invalidateCache);
      }
    }

    return responseData;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Authenticated POST request timed out for URL:', url);
      // Stop keep-alive on timeout
      try {
        const keepAliveService = (await import('./keepAliveService')).default;
        if (keepAliveService.getActive()) {
          keepAliveService.stop();
          console.log('[API Service] Stopped keep-alive service due to timeout');
        }
      } catch (keepAliveError) {
        // Ignore errors when stopping keep-alive
      }
      throw new Error(`Request timed out after ${timeout || 'unknown'}ms`);
    }
    console.error('Error in authPost:', error, 'for URL:', url);
    
    // Stop keep-alive on error
    try {
      const keepAliveService = (await import('./keepAliveService')).default;
      if (keepAliveService.getActive()) {
        keepAliveService.stop();
        console.log('[API Service] Stopped keep-alive service due to error');
      }
    } catch (keepAliveError) {
      // Ignore errors when stopping keep-alive
    }

    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in POST request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

/**
 * Make an authenticated PUT request
 * @param {String} url - URL to fetch
 * @param {Object} data - Data to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const authPut = async (url, data, options = {}) => {
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    
    console.log('Making authenticated PUT request to:', fullUrl);
    console.log('PUT request data:', data);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for PUT request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Make the request
    console.log('Sending PUT request with auth token');
    const response = await fetch(fullUrl, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    console.log('PUT response status:', response.status, 'for URL:', url);
    
    // Check if response is ok
    if (!response.ok) {
      console.error('PUT response not OK:', response.status, response.statusText, 'for URL:', url);

      if (response.status === 401) {
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from PUT request:', errorData);

        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response
    const responseData = await response.json();
    console.log('PUT request successful for URL:', url);

    // Invalidate related cache entries if specified
    if (options.invalidateCache) {
      if (Array.isArray(options.invalidateCache)) {
        options.invalidateCache.forEach(pattern => invalidateCache(pattern));
      } else {
        invalidateCache(options.invalidateCache);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Error in authPut:', error, 'for URL:', url);
    
    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in PUT request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }
};

/**
 * Make an authenticated DELETE request
 * @param {String} url - URL to fetch
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const authDelete = async (url, options = {}) => {
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    
    console.log('Making authenticated DELETE request to:', fullUrl);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for DELETE request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Make the request
    console.log('Sending DELETE request with auth token');
    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('DELETE response status:', response.status, 'for URL:', url);
    
    // Check if response is ok
    if (!response.ok) {
      console.error('DELETE response not OK:', response.status, response.statusText, 'for URL:', url);

      if (response.status === 401) {
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from DELETE request:', errorData);

        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response
    const responseData = await response.json();
    console.log('DELETE request successful for URL:', url);

    // Invalidate related cache entries if specified
    if (options.invalidateCache) {
      if (Array.isArray(options.invalidateCache)) {
        options.invalidateCache.forEach(pattern => invalidateCache(pattern));
      } else {
        invalidateCache(options.invalidateCache);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Error in authDelete:', error, 'for URL:', url);
    
    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in DELETE request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }
};

/**
 * Get comments for a work item
 * @param {String} workItemId - Work item ID
 * @returns {Promise<Object>} - Comments data
 * @throws {Error} - If the request fails
 */
export const getComments = async (workItemId) => {
  console.log('Getting comments for work item:', workItemId);
  return authGet(`/api/comments/${workItemId}`);
};

/**
 * Add a comment to a work item
 * @param {String} workItemId - Work item ID
 * @param {Object|String} commentData - Comment data or content string
 * @returns {Promise<Object>} - Added comment data
 * @throws {Error} - If the request fails
 */
export const addComment = async (workItemId, commentData) => {
  console.log('Adding comment to work item:', workItemId);
  
  // Handle both string content and object with content
  const data = typeof commentData === 'string' 
    ? { content: commentData } 
    : commentData;
    
  console.log('Comment data being sent:', data);
  
  return authPost(`/api/comments/${workItemId}`, data);
};

/**
 * Make an authenticated PATCH request
 * @param {String} url - URL to fetch
 * @param {Object} data - Data to send
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const authPatch = async (url, data) => {
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    
    console.log('Making authenticated PATCH request to:', fullUrl);
    console.log('PATCH request data:', data);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for PATCH request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Make the request
    console.log('Sending PATCH request with auth token');
    const response = await fetch(fullUrl, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    console.log('PATCH response status:', response.status, 'for URL:', url);
    
    // Check if response is ok
    if (!response.ok) {
      console.error('PATCH response not OK:', response.status, response.statusText, 'for URL:', url);

      if (response.status === 401) {
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      }

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from PATCH request:', errorData);

        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response
    const responseData = await response.json();
    console.log('PATCH request successful for URL:', url);
    
    return responseData;
  } catch (error) {
    console.error('Error in authPatch:', error, 'for URL:', url);
    
    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in PATCH request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }
};

/**
 * Delete a comment
 * @param {String} commentId - Comment ID
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const deleteComment = async (commentId) => {
  console.log('Deleting comment:', commentId);
  return authDelete(`/api/comments/${commentId}`);
};

/**
 * Get all WSR items
 * @param {Object} options - Optional: { weeklyReportOnly: true } to get only items in Weekly Report tab
 * @returns {Promise<Object>} - WSR items data
 * @throws {Error} - If the request fails
 */
export const getWSRItems = async (options = {}) => {
  const params = options.weeklyReportOnly ? '?weeklyReportOnly=true' : '';
  return get(`/api/wsr${params}`);
};

/**
 * Get Epics/Features with Target dates in current quarter (from EpicHierarchy + WorkItem).
 * Used by WSR view to show items directly without requiring auto-populate.
 * @returns {Promise<Object>} - { data: [{ adoId }, ...], quarter }
 */
export const getWSRCurrentQuarterItems = async () => {
  return get('/api/wsr/current-quarter-items');
};

/**
 * Add ADO item to WSR
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - Added WSR item data
 * @throws {Error} - If the request fails
 */
export const addWSRItem = async (adoId) => {
  console.log('Adding WSR item:', adoId);
  return authPost('/api/wsr', { adoId });
};

/**
 * Remove ADO item from WSR
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - Deletion result
 * @throws {Error} - If the request fails
 */
export const removeWSRItem = async (adoId) => {
  console.log('Removing WSR item:', adoId);
  return authDelete(`/api/wsr/${adoId}`);
};

/**
 * Move ADO item to Weekly Report tab
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - Updated WSR item
 * @throws {Error} - If the request fails
 */
export const moveToWeeklyReport = async (adoId) => {
  return authPost(`/api/wsr/${adoId}/move-to-weekly-report`, {});
};

/**
 * Remove ADO item from Weekly Report tab only (item stays in WSR pool)
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - Updated WSR item
 * @throws {Error} - If the request fails
 */
export const removeFromWeeklyReport = async (adoId) => {
  return authDelete(`/api/wsr/${adoId}/from-weekly-report`);
};

/**
 * Get all planning quarter assignments (adoId -> quarter)
 * @param {Object} options - { useCache: boolean } - set useCache: false to skip cache (e.g. after a move)
 * @returns {Promise<Object>} - { success, data: [{ adoId, quarter }] }
 */
export const getPlanningItems = async (options = {}) => {
  return get('/api/planning', null, 'json', options);
};

/**
 * Set or clear quarter for an ADO item (move to Quarter tab or back to stream)
 * @param {String} adoId - ADO work item ID
 * @param {String|null} quarter - 'Q1' | 'Q2' | 'Q3' | 'Q4' | null (null = move to stream)
 * @returns {Promise<Object>} - Updated planning item
 */
export const setPlanningQuarter = async (adoId, quarter) => {
  return authPatch(`/api/planning/${adoId}/quarter`, { quarter });
};

/**
 * Reset all planning moves so items align to quarters by target date (admin only).
 * @returns {Promise<Object>} - { success, message, data: { deletedCount } }
 */
export const resetPlanningToTargetDate = async () => {
  return authPost('/api/planning/reset', {});
};

/**
 * Get work items with target date in current quarter (Planning tab).
 * @returns {Promise<Object>} - { data: [{ adoId }, ...], quarter, quarterStart, quarterEnd }
 */
export const getPlanningCurrentQuarterItems = async () => {
  return get('/api/planning/current-quarter-items');
};

/**
 * Get work item details for Planning tab (includes state).
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - { success, data: { adoId, title, workItemType, team, state, ... } }
 */
export const getPlanningItemDetails = async (adoId) => {
  return get(`/api/planning/${adoId}/details`);
};

/**
 * Update WSR item (e.g. status: Green, Amber, Red)
 * @param {String} adoId - ADO work item ID
 * @param {Object} payload - { status: 'Green' | 'Amber' | 'Red' }
 * @returns {Promise<Object>} - Updated WSR item
 */
export const updateWSRItem = async (adoId, payload) => {
  return authPatch(`/api/wsr/${adoId}`, payload);
};

/**
 * Get work item details for WSR
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} - Work item details
 * @throws {Error} - If the request fails
 */
export const getWSRItemDetails = async (adoId) => {
  console.log('Getting WSR item details:', adoId);
  return get(`/api/wsr/${adoId}/details`);
};

/**
 * Get bugs list for a WSR work item (and its descendants)
 * @param {string} adoId - ADO work item ID
 * @returns {Promise<Object>} - { success, data: Array<{ id, title, state, source }> }
 */
export const getWSRItemBugs = async (adoId) => {
  return get(`/api/wsr/${adoId}/bugs`);
};

/**
 * Get test cases list for a WSR work item (and its descendants)
 * @param {string} adoId - ADO work item ID
 * @returns {Promise<Object>} - { success, data: Array<{ id, title, state }> }
 */
export const getWSRItemTestCases = async (adoId) => {
  return get(`/api/wsr/${adoId}/test-cases`);
};

/**
 * Refresh a single WSR item from ADO and update work items collection
 * @param {string} adoId - ADO work item ID
 * @returns {Promise<Object>} - Refresh result
 * @throws {Error} - If the request fails
 */
export const refreshWSRItem = async (adoId) => {
  console.log('Refreshing WSR item from ADO:', adoId);
  return authPost(`/api/wsr/${adoId}/refresh`, {});
};

/**
 * Refresh all WSR items from ADO (runs in background). Returns immediately with jobId; poll getWSRRefreshStatus(jobId) until completed.
 * @returns {Promise<Object>} - { jobId, data: { total } } on 202, or legacy result on 200
 * @throws {Error} - If the request fails
 */
export const refreshWSRItems = async () => {
  console.log('Refreshing WSR items from ADO...');
  return authPost('/api/wsr/refresh', {});
};

/**
 * Get WSR refresh job status (for polling after refreshWSRItems returns jobId).
 * @param {string} jobId - Job ID from refreshWSRItems response
 * @returns {Promise<Object>} - { status, progress, message, result, error }
 */
export const getWSRRefreshStatus = async (jobId) => {
  return authGet(`/api/wsr/refresh-status/${encodeURIComponent(jobId)}`);
};

/**
 * Auto-populate WSR items from ADO Features and Epics with Target dates in current quarter
 * @returns {Promise<Object>} - Auto-populate result
 * @throws {Error} - If the request fails
 */
export const autoPopulateWSRItems = async () => {
  console.log('Auto-populating WSR items from ADO...');
  return authPost('/api/wsr/auto-populate', {});
};

/**
 * Generate Weekly Report PDF and store on server (timestamp in filename)
 * @returns {Promise<Object>} - { filename, generatedAt }
 */
export const generateWSRReport = async (options = {}) => {
  const body = { ...options };
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    body.baseUrl = window.location.origin;
  }
  return authPost('/api/wsr/generate-report', body);
};

/**
 * List generated WSR report PDFs
 * @returns {Promise<Object>} - { data: Array<{ filename, generatedAt }> }
 */
export const listWSRReports = async () => {
  return get('/api/wsr/reports');
};

/**
 * Get WSR report generation log (plain text)
 * @param {string} logFilename - Log filename (e.g. wsr-report-2026-02-09T12-00-00.log)
 * @returns {Promise<string>} - Log content as string
 */
export const getWSRReportLog = async (logFilename) => {
  return authGet(`/api/wsr/reports/${encodeURIComponent(logFilename)}`, null, 'text', { useCache: false });
};

/**
 * Delete a generated WSR report PDF
 * @param {string} filename - Report filename (e.g. wsr-2025-01-29.pdf)
 * @returns {Promise<Object>} - { success, message }
 */
export const deleteWSRReport = async (filename) => {
  return authDelete(`/api/wsr/reports/${encodeURIComponent(filename)}`);
};

/**
 * Get work item hierarchy
 * @param {Object} filters - Filters to apply
 * @returns {Promise<Array>} - Work item hierarchy
 * @throws {Error} - If the request fails
 */
export const getWorkItemHierarchy = async (filters = {}) => {
  console.log('Getting work item hierarchy with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/workitems/hierarchy${queryString ? `?${queryString}` : ''}`;
  
  return authGet(url);
};

/**
 * Get dashboard metrics
 * @param {String} url - Optional custom URL for the request
 * @returns {Promise<Object>} - Dashboard metrics
 * @throws {Error} - If the request fails
 */
export const getDashboardMetrics = async (url = null) => {
  console.log('Getting dashboard metrics from API');
  return authGet(url || '/api/dashboard/metrics');
};

/**
 * Get completion metrics
 * @returns {Promise<Object>} - Completion metrics
 * @throws {Error} - If the request fails
 */
export const getCompletionMetrics = async () => {
  console.log('Getting completion metrics from API');
  return authGet('/api/dashboard/completion');
};

/**
 * Get work items for a specific quality metric
 * @param {String} metricType - Type of metric (epics, features, pbis)
 * @returns {Promise<Array>} - Array of work items for the specified metric
 * @throws {Error} - If the request fails
 */
export const getQualityMetricWorkItems = async (metricType) => {
  console.log(`Getting work items for quality metric: ${metricType}`);
  return authGet(`/api/dashboard/quality-metrics/${metricType}`);
};

/**
 * Get epics with optional filters
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of epics
 * @throws {Error} - If the request fails
 */
export const getEpics = async (filters = {}, useAuth = true) => {
  console.log('Getting epics from API with filters:', filters);
  
  // If requesting epics with target dates and not using auth, use public endpoint
  if (filters.hasTargetDate === 'true' && !useAuth) {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'hasTargetDate') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const url = `/api/epics/public/with-target-dates${queryString ? `?${queryString}` : ''}`;
    const response = await get(url);
    return response.data || response;
  }
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/epics${queryString ? `?${queryString}` : ''}`;
  
  // Use public get for public pages, authGet for authenticated pages
  const response = useAuth ? await authGet(url) : await get(url);
  return response.data || response;
};

/**
 * Get timeline configuration
 * @returns {Promise<Object>} - Timeline configuration
 * @throws {Error} - If the request fails
 */
export const getTimelineConfig = async () => {
  const response = await get('/api/timeline/config');
  return response.data;
};

/**
 * Update timeline configuration (admin only)
 * @param {Object} config - Timeline configuration { startQuarter, startYear, endQuarter, endYear }
 * @returns {Promise<Object>} - Updated timeline configuration
 * @throws {Error} - If the request fails
 */
export const updateTimelineConfig = async (config) => {
  const response = await authPut('/api/timeline/config', config);
  return response.data;
};

/**
 * Clone or pull all repositories into BUG_ANALYSIS_REPOS_DIR (admin; long-running).
 * @returns {Promise<{ success: boolean, data?: { reposDir: string, results: object[] }, message?: string }>}
 */
export const refreshBugAnalysisRepos = () =>
  authPost('/api/admin/bug-analysis/refresh-repos', {}, { timeout: 30 * 60 * 1000 });

/**
 * Get release calendar events for a year (public)
 * @param {Number} year - Year (e.g. 2026)
 * @param {Object} options - Optional { useCache: false } to force fresh data after add/edit/delete
 * @returns {Promise<Array>} - Array of release calendar events
 */
export const getReleaseCalendarEvents = async (year, options = {}) => {
  const response = await authGet('/api/release-calendar/events', { year }, 'json', options);
  return response.data || [];
};

/**
 * Create a release calendar event (QA Manager or Admin only)
 * @param {Object} data - { date, eventType }
 * @returns {Promise<Object>} - Created event
 */
export const createReleaseCalendarEvent = async (data) => {
  const response = await authPost('/api/release-calendar/events', data);
  return response.data;
};

/**
 * Update a release calendar event (QA Manager or Admin only)
 * @param {String} id - Event ID
 * @param {Object} data - { date?, eventType? }
 * @returns {Promise<Object>} - Updated event
 */
export const updateReleaseCalendarEvent = async (id, data) => {
  const response = await authPut(`/api/release-calendar/events/${id}`, data);
  return response.data;
};

/**
 * Delete a release calendar event (QA Manager or Admin only)
 * @param {String} id - Event ID
 * @returns {Promise<void>}
 */
export const deleteReleaseCalendarEvent = async (id) => {
  await authDelete(`/api/release-calendar/events/${id}`);
};

/**
 * Get stored customer instances (CSV data) for release calendar
 * @returns {Promise<{ columns: string[], rows: object[] }>}
 */
export const getCustomerInstances = async () => {
  const response = await authGet('/api/release-calendar/customer-instances', null, 'json', {
    useCache: true,
    cacheTTL: 45
  });
  const data = response?.data ?? {};
  return {
    columns: data.columns ?? [],
    rows: data.rows ?? []
  };
};

/**
 * Save (replace) customer instances CSV data
 * @param {Object} payload - { columns: string[], rows: object[] }
 * @returns {Promise<{ columns: string[], rows: object[] }>}
 */
export const saveCustomerInstances = async (payload) => {
  const response = await authPost('/api/release-calendar/customer-instances', payload);
  const data = response?.data ?? {};
  return { columns: data.columns ?? [], rows: data.rows ?? [] };
};

/**
 * Refresh getCBV.action for customer instances; updates each document in DB and returns updated data. Admin only.
 * @param {Object} [options] - Optional. { onlyFailed: true } to refresh only rows with version NR.
 * @returns {Promise<{ columns: string[], rows: object[] }>}
 */
export const refreshCustomerInstancesCBV = async (options = {}) => {
  const response = await authPost('/api/release-calendar/customer-instances/refresh-cbv', {
    onlyFailed: !!options.onlyFailed
  });
  const data = response?.data ?? {};
  return { columns: data.columns ?? [], rows: data.rows ?? [] };
};

/**
 * Refresh getMSV.action for all customer instances. Admin only.
 * @param {{ onlyFailed?: boolean }} [options] - If onlyFailed: true, only refresh instances with missing/failed MSV data
 * @returns {Promise<{ columns: string[], rows: object[] }>}
 */
export const refreshCustomerInstancesMSV = async (options = {}) => {
  const response = await authPost('/api/release-calendar/customer-instances/refresh-msv', options);
  const data = response?.data ?? {};
  return { columns: data.columns ?? [], rows: data.rows ?? [] };
};

/**
 * Refresh checkAzure.action for all customer instances. Admin only.
 * @param {{ onlyFailed?: boolean }} [options] - If onlyFailed: true, only refresh instances with missing/failed Azure data
 * @returns {Promise<{ columns: string[], rows: object[] }>}
 */
export const refreshCustomerInstancesAzure = async (options = {}) => {
  const response = await authPost('/api/release-calendar/customer-instances/refresh-azure', options);
  const data = response?.data ?? {};
  return { columns: data.columns ?? [], rows: data.rows ?? [] };
};

export const refreshCustomerInstancesBulk = async (payload = {}) => {
  return authPost('/api/release-calendar/customer-instances/refresh-bulk', payload);
};

export const recheckPlatformInvestigationTicket = async (workItemId) =>
  authPost(`/api/platform-investigation/${workItemId}/recheck`, {});

export const refreshCdnInstances = async () =>
  authPost('/api/release-calendar/customer-instances/cdn-refresh', {});

export const refreshCdnInstance = async (instanceId) =>
  authPost(`/api/release-calendar/customer-instances/${instanceId}/cdn-refresh`, {});

export const getCustomerInstancesBulkProgress = async () => {
  return authGet('/api/release-calendar/customer-instances/bulk-progress', {}, 'json', { useCache: false });
};

export const stopCustomerInstancesBulk = async () =>
  authPost('/api/release-calendar/customer-instances/stop-bulk', {});

/**
 * Fetch getCBV for one customer instance row and store in document. Admin only.
 * @param {string} rowId - Customer instance row document _id
 * @returns {Promise<object>} Updated row data
 */
export const fetchCustomerInstanceRowCBV = async (rowId) => {
  const response = await authPost(`/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}/get-cbv`, {});
  return response?.data ?? {};
};

/**
 * Fetch getMSV for one customer instance row and store in document. Admin only.
 */
export const fetchCustomerInstanceRowMSV = async (rowId) => {
  const response = await authPost(`/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}/get-msv`, {});
  return response?.data ?? {};
};

/**
 * Fetch checkAzure for one customer instance row and store in document. Admin only.
 */
export const fetchCustomerInstanceRowAzure = async (rowId) => {
  const response = await authPost(`/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}/check-azure`, {});
  return response?.data ?? {};
};

/**
 * Update a single customer instance row. Admin only. CIAM/Wijmo/Instance type are fetch-only (Azure).
 * @param {string} rowId - Mongo _id of the instance doc
 * @param {Object} payload - { instanceName?, instanceDomain?, currentVersion?, region?, group?, friendlyName?, extra? }
 * @returns {Promise<Object>} - Updated row (display shape)
 */
export const updateCustomerInstanceRow = async (rowId, payload) => {
  const response = await authPatch(`/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}`, payload);
  return response?.data ?? {};
};

/**
 * Add a single new customer instance row manually. Admin only.
 * @param {Object} payload - { instanceName, instanceDomain?, friendlyName?, currentVersion?, region?, group? }
 * @returns {Promise<Object>} - New row (display shape)
 */
export const addCustomerInstanceRow = async (payload) => {
  const response = await authPost('/api/release-calendar/customer-instances/add-row', payload);
  return response?.data ?? {};
};

/**
 * Permanently delete a single customer instance row. Admin only.
 * @param {string} rowId - Mongo _id of the instance doc
 * @returns {Promise<void>}
 */
export const deleteCustomerInstanceRow = async (rowId) => {
  await authDelete(`/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}`);
};

/**
 * History of Max Memory / Max Threads / Max DB Connections when CBV sync recorded a change.
 * @param {string} rowId - Mongo _id of the instance doc
 * @returns {Promise<{ instanceName: string, friendlyName: string, entries: Array<{ recordedAt: string, maxMemory: number|null, totalThreads: number|null, maxDbConnections: number|null }> }>}
 */
export const getCustomerInstanceCbvResourceHistory = async (rowId) => {
  const response = await authGet(
    `/api/release-calendar/customer-instances/row/${encodeURIComponent(rowId)}/cbv-resource-history`,
    null,
    'json',
    { useCache: false }
  );
  const data = response?.data ?? {};
  return {
    instanceName: data.instanceName ?? '',
    friendlyName: data.friendlyName ?? '',
    entries: Array.isArray(data.entries) ? data.entries : []
  };
};

/**
 * Persisted heap log analysis (Datadog) for all instances + optional bulk-refresh checkpoint.
 * @returns {Promise<{ entries: Record<string, object>, bulkProgress: { keys: string[], nextIndex: number } | null }>}
 */
/**
 * Daily cron status for production analysis (memory + thread + DB). Public GET.
 * @returns {Promise<object|null>}
 */
export const getProductionAnalysisSchedulerStatus = async () => {
  const res = await authGet('/api/release-calendar/production-analysis-scheduler/status', null, 'json', { useCache: false });
  return res?.data ?? null;
};

/** Daily recommendation band snapshots: customers whose status changed between snapshot days. */
export const getRecommendationLevelTransitions = async (analysisType, days = 30) => {
  const res = await authGet(
    '/api/release-calendar/recommendation-level-snapshots/transitions',
    { analysisType, days },
    'json',
    { useCache: false }
  );
  return res?.data ?? null;
};

/** Resource upgrade events with before/after violation counts and recommendation levels. */
export const getProgressReport = async (days = 90) => {
  const res = await authGet('/api/release-calendar/progress-report', { days }, 'json', { useCache: false });
  return res ?? null;
};


/** Per-customer daily series of recommendation levels (for charts). */
export const getRecommendationLevelSeries = async (stableKey, analysisType, days = 90) => {
  const res = await authGet(
    '/api/release-calendar/recommendation-level-snapshots/series',
    { stableKey, analysisType, days },
    'json',
    { useCache: false }
  );
  return res?.data ?? null;
};

export const getHeapRecommendations = async () => {
  const response = await authGet('/api/release-calendar/heap-recommendations', null, 'json', { useCache: false });
  const data = response?.data ?? {};
  return {
    entries: data.entries && typeof data.entries === 'object' ? data.entries : {},
    bulkProgress: data.bulkProgress ?? null
  };
};

/**
 * Recompute heap recommendation for one instance (Datadog logs). Requires auth.
 * @param {{ row?: object, days?: number, instanceName?: string }} payload
 */
export const refreshHeapRecommendationOne = async (payload) => {
  // Datadog Logs Search can run for several minutes across chunked time windows; align with dev proxy (10m).
  return authPost('/api/release-calendar/heap-recommendations/refresh', payload, { timeout: 600000 });
};

/**
 * Start or resume bulk heap refresh. Requires auth. For resume, pass resume: true and the same rows array as the original run.
 */
export const refreshHeapRecommendationAll = async (payload) => {
  return authPost('/api/release-calendar/heap-recommendations/refresh-all', payload, { timeout: 120000 });
};

/**
 * Clear or set bulk progress checkpoint (admin tooling).
 */
export const putHeapRecommendationBulkProgress = async (payload) => {
  return authPut('/api/release-calendar/heap-recommendations/bulk-progress', payload);
};

/**
 * Persisted DB connection (@CADBC) analysis per instance + optional bulk checkpoint.
 */
export const getDbConnectionRecommendations = async () => {
  const response = await authGet('/api/release-calendar/db-connection-recommendations', null, 'json', { useCache: false });
  const data = response?.data ?? {};
  return {
    entries: data.entries && typeof data.entries === 'object' ? data.entries : {},
    bulkProgress: data.bulkProgress ?? null
  };
};

export const refreshDbConnectionRecommendationOne = async (payload) => {
  return authPost('/api/release-calendar/db-connection-recommendations/refresh', payload, { timeout: 600000 });
};

export const refreshDbConnectionRecommendationAll = async (payload) => {
  return authPost('/api/release-calendar/db-connection-recommendations/refresh-all', payload, { timeout: 120000 });
};

export const putDbConnectionRecommendationBulkProgress = async (payload) => {
  return authPut('/api/release-calendar/db-connection-recommendations/bulk-progress', payload);
};

/**
 * Tomcat thread (@AT) analysis per instance + bulk checkpoint.
 */
export const getThreadRecommendations = async () => {
  const response = await authGet('/api/release-calendar/thread-recommendations', null, 'json', { useCache: false });
  const data = response?.data ?? {};
  return {
    entries: data.entries && typeof data.entries === 'object' ? data.entries : {},
    bulkProgress: data.bulkProgress ?? null
  };
};

export const refreshThreadRecommendationOne = async (payload) => {
  return authPost('/api/release-calendar/thread-recommendations/refresh', payload, { timeout: 600000 });
};

export const refreshThreadRecommendationAll = async (payload) => {
  return authPost('/api/release-calendar/thread-recommendations/refresh-all', payload, { timeout: 120000 });
};

export const putThreadRecommendationBulkProgress = async (payload) => {
  return authPut('/api/release-calendar/thread-recommendations/bulk-progress', payload);
};

/** Production analysis: per-instance comment + ServiceNow per analysis type (memory / threads / DB). */
export const getProductionAnalysisNotes = async () => {
  const response = await authGet('/api/release-calendar/production-analysis-notes', null, 'json', { useCache: false });
  const notes = response?.notes;
  return notes && typeof notes === 'object' ? notes : {};
};

/** @param {{ stableKey: string, analysisType: 'memory'|'threads'|'db', comment?: string, serviceNowNumber?: string }} payload */
export const upsertProductionAnalysisNote = async (payload) => {
  return authPut('/api/release-calendar/production-analysis-notes', payload);
};

/**
 * Same ServiceNow # / comment for many clients at once (current analysis tab).
 * @param {{ stableKeys: string[], analysisType: 'memory'|'threads'|'db', comment?: string, serviceNowNumber?: string }} payload
 */
export const bulkUpsertProductionAnalysisNotes = async (payload) => {
  return authPost('/api/release-calendar/production-analysis-notes/bulk', payload);
};

/** Refresh ServiceNow raised/executed for the given analysis dimension (ticket # stored on that slice). */
export const refreshProductionAnalysisSnow = async (stableKeys, analysisType) => {
  return authPost('/api/release-calendar/production-analysis-notes/refresh-snow', { stableKeys, analysisType });
};

/**
 * Get features with optional filters
 * @param {String} epicId - Optional epic ID to filter by
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of features
 * @throws {Error} - If the request fails
 */
export const getFeatures = async (epicId = null, filters = {}) => {
  console.log('Getting features from API with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add epicId to query params if provided
  if (epicId) {
    queryParams.append('epicId', epicId);
  }
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/epics/features${queryString ? `?${queryString}` : ''}`;
  
  const response = await authGet(url);
  return response.data;
};

// Default export for convenience - will be updated at the end of the file

/**
 * Delete a fix version
 * @param {String} id - Fix version ID
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
/**
 * Delete a fix version
 * @param {String} version - Fix version string (e.g., "5.9.14") - used instead of _id for stability
 * @returns {Promise<Object>} - Deletion result
 * @throws {Error} - If the request fails
 */
export const deleteFixVersion = async (version) => {
  console.log(`Deleting fix version with version: ${version}`);
  // URL encode the version to handle any special characters
  const encodedVersion = encodeURIComponent(version);
  return authDelete(`/api/fix-versions/${encodedVersion}`);
};

/**
 * Get the Go/No-Go sign-off record for a release (public read).
 * @param {String} version - Release version, e.g. "5.11.01"
 * @returns {Promise<Object>} - Sign-off record (default empty shape if none saved yet)
 */
export const getReleaseSignOff = async (version) => {
  const response = await get(`/api/fix-versions/${encodeURIComponent(version)}/sign-off`, null, 'json', { useCache: false });
  return response.data;
};

/**
 * Update a release sign-off (requires authentication). Pass EITHER a role toggle
 * ({ role, approved, metricsSnapshot }) OR the final decision ({ finalDecision, metricsSnapshot }).
 * Server records who/when from the authenticated user — do not send identity from the client.
 * @param {String} version - Release version
 * @param {Object} payload - { role?, approved?, finalDecision?, metricsSnapshot? }
 * @returns {Promise<Object>} - Updated sign-off record
 */
export const saveReleaseSignOff = async (version, payload) => {
  const response = await authPatch(`/api/fix-versions/${encodeURIComponent(version)}/sign-off`, payload);
  return response.data;
};

/**
 * Upsert one or more microservice artifact rows for a release (requires authentication).
 * Each row is matched by `name`; the server stamps who/when on every touched row.
 * @param {String} version - Release version
 * @param {Array<{name: string, rcTag?: string, needsUpgrade?: boolean, applied?: boolean}>} microservices
 * @returns {Promise<Object>} - Updated sign-off record (incl. microservices)
 */
export const saveReleaseMicroservices = async (version, microservices) => {
  const response = await authPatch(`/api/fix-versions/${encodeURIComponent(version)}/sign-off`, { microservices });
  return response.data;
};

/**
 * Delete a microservice artifact row by name (requires authentication).
 * @param {String} version - Release version
 * @param {String} name - Microservice name to remove
 * @returns {Promise<Object>} - Updated sign-off record
 */
export const deleteReleaseMicroservice = async (version, name) => {
  const response = await authPatch(`/api/fix-versions/${encodeURIComponent(version)}/sign-off`, { microserviceDelete: name });
  return response.data;
};

/**
 * Generate release notes for a fix version
 * @param {String} id - Fix version ID
 * @param {Array} items - Array of work items with summaries
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const generateReleaseNotes = async (id, items, version = null) => {
  console.log(`Generating release notes for fix version with ID: ${id}${version ? `, version: ${version}` : ''}`);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/fix-versions/${id}/release-notes`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items, version })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `Request failed with status: ${response.status}` }));
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }
    
    // Check if response is a PDF (blob)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/pdf')) {
      // Return blob for PDF download
      const blob = await response.blob();
      return { success: true, blob, version };
    }
    
    // Otherwise return JSON response
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating release notes:', error);
    throw error;
  }
};

/**
 * Get release notes PDF URL
 * @param {String} id - Fix version ID
 * @returns {String} - PDF URL
 */
export const getReleaseNotesPdfUrl = (id) => {
  return `/api/fix-versions/${id}/release-notes-pdf`;
};

/**
 * Delete release notes for a fix version
 * @param {String} id - Fix version ID
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const deleteReleaseNotes = async (id) => {
  console.log(`Deleting release notes for fix version with ID: ${id}`);
  return authDelete(`/api/fix-versions/${id}/release-notes`);
};

/**
 * Get work items with optional filters
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Object>} - Work items data
 * @throws {Error} - If the request fails
 */
export const fetchWorkItemsByFixVersion = async (fixVersion, fetchPRs = true) => {
  console.log('Fetching work items from ADO for fixVersion:', fixVersion);
  // No timeout - this is a long-running operation that can take significant time
  return authPost('/api/workitems/fetch-by-fixversion', { fixVersion, fetchPRs }, { timeout: null });
};

/**
 * Get pull requests for all work items in a fix version
 * @param {String} fixVersion - Fix version string
 * @returns {Promise<Object>} - Pull requests data
 */
export const getPullRequestsByFixVersion = async (fixVersion) => {
  console.log('Getting pull requests for fixVersion:', fixVersion);
  return get(`/api/workitems/fixversion/${encodeURIComponent(fixVersion)}/pull-requests`, false);
};

export const getWorkItems = async (filters = {}) => {
  console.log('Getting work items from API with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/workitems${queryString ? `?${queryString}` : ''}`;
  
  return authGet(url);
};

/**
 * Get production bugs with optional filters
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Object>} - Production bugs data
 * @throws {Error} - If the request fails
 */
export const getProductionBugs = async (filters = {}) => {
  console.log('Getting production bugs from API with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/workitems/production-bugs${queryString ? `?${queryString}` : ''}`;
  
  return authGet(url);
};

/**
 * Get cycle time metrics by month for production bugs
 * @param {Array} priorities - Optional array of priorities to filter by (e.g., ['P1', 'P2'])
 * @returns {Promise<Object>} - Cycle time metrics data
 * @throws {Error} - If the request fails
 */
export const getBugCycleTimeMetrics = async (priorities = null) => {
  try {
    console.log('Getting bug cycle time metrics', { priorities });
    const baseUrl = await getCurrentBaseUrl();
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (priorities && Array.isArray(priorities) && priorities.length > 0) {
      priorities.forEach(priority => {
        queryParams.append('priority', priority);
      });
    }
    
    const queryString = queryParams.toString();
    const fullUrl = `${baseUrl}/api/workitems/production-bugs/cycle-time-metrics${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }
    
    const responseData = await response.json();
    return responseData;
    
  } catch (error) {
    console.error('Error getting bug cycle time metrics:', error);
    throw error;
  }
};

/**
 * Get internal bugs with optional filters
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Object>} - Internal bugs data
 * @throws {Error} - If the request fails
 */
export const getInternalBugs = async (filters = {}) => {
  console.log('Getting internal bugs from API with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/workitems/internal-bugs${queryString ? `?${queryString}` : ''}`;
  
  return authGet(url);
};

/**
 * Get Internal Bugs page sync config (query URL, last synced time).
 * @returns {Promise<{ queryUrl: string, syncedAt: string|null }>}
 */
export const getInternalBugsSyncConfig = async () => {
  const response = await authGet('/api/workitems/internal-bugs/sync-config');
  if (response && response.success && response.data) {
    return response.data;
  }
  return { queryUrl: '', syncedAt: null };
};

/**
 * Update Internal Bugs sync config (query URL). Admin only.
 * @param {string} queryUrl
 * @returns {Promise<{ queryUrl: string }>}
 */
export const updateInternalBugsSyncConfig = async (queryUrl) => {
  const response = await authPut('/api/workitems/internal-bugs/sync-config', { queryUrl: (queryUrl || '').trim() });
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to save sync config');
};

/**
 * Sync internal bugs from ADO query URL. Runs query and upserts into Internal Bug collection. Admin only.
 * @param {string} queryUrl - Full ADO query URL
 * @returns {Promise<{ queryUrl: string, count: number, created: number, updated: number }>}
 */
export const syncInternalBugsFromQuery = async (queryUrl) => {
  const url = '/api/workitems/internal-bugs/sync-from-query';
  const data = { queryUrl: (queryUrl || '').trim() };
  const options = { timeout: 120000 };
  const response = await authPost(url, data, options);
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || response?.error || 'Failed to sync from query');
};

/**
 * Run ADO saved query by URL and return table columns and rows (for Internal Bugs tab)
 * Uses optional auth endpoint so works with or without login
 * @param {string} queryUrl - Full ADO query URL (e.g. .../_queries/query/{guid})
 * @returns {Promise<{ columns: string[], rows: Array<Object> }>}
 */
export const syncAdoQueryResults = async (queryUrl) => {
  const url = '/api/workitems/ado-query-results';
  const data = { queryUrl: (queryUrl || '').trim() };
  const options = { timeout: 120000 };
  const response = await post(url, data, options);
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to sync ADO query results');
};

/**
 * Run ADO filter query and return rows with A11Y analysis columns (Cycle time, Activated on, Resolved on, Time to Fix)
 * @param {string} queryUrl - Full ADO query/filter URL
 * @returns {Promise<{ queryUrl: string, columns: string[], rows: Array<Object> }>}
 */
/**
 * @param {string} queryUrl - Full ADO filter/query URL
 * @param {{ limit?: number }} [options] - Optional. limit: max items to process (e.g. 100 for testing)
 */
export const fetchA11yBugAnalysis = async (queryUrl, options = {}) => {
  const url = '/api/workitems/a11y-bug-analysis';
  const data = { queryUrl: (queryUrl || '').trim() };
  if (typeof options.limit === 'number' && options.limit > 0) {
    data.limit = options.limit;
  }
  const requestOptions = { timeout: 300000 }; // 5 min - history fetch can be slow for many items
  const response = await post(url, data, requestOptions);
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to fetch A11Y bug analysis');
};

/**
 * Get persisted A11Y bug analysis (last saved query URL and result from a11y-bugs collection)
 * @returns {Promise<{ queryUrl: string, syncedAt: string|null, columns: string[], rows: Array<Object> }>}
 */
export const getA11yBugs = async () => {
  const response = await get('/api/workitems/a11y-bugs');
  if (response && response.success && response.data) {
    return response.data;
  }
  return { queryUrl: '', syncedAt: null, columns: [], rows: [] };
};

/**
 * Sync Eng-TSI work items from an ADO filter into eng-tsi-items
 * @param {string} queryUrl - Saved query URL, work items URL with wiql=, or raw WIQL
 */
export const syncEngTsiItems = async (queryUrl) => {
  const url = '/api/workitems/eng-tsi-items/sync';
  const data = { queryUrl: (queryUrl || '').trim() };
  const requestOptions = { timeout: 300000 };
  const response = await post(url, data, requestOptions);
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to sync Eng-TSI items from ADO');
};

/**
 * Get persisted Eng-TSI rows (last sync from eng-tsi-items)
 */
export const getEngTsiItems = async () => {
  const response = await get('/api/workitems/eng-tsi-items');
  if (response && response.success && response.data) {
    return response.data;
  }
  return { queryUrl: '', syncedAt: null, columns: [], rows: [] };
};

/**
 * Batch-resolve Code Fix on closed production bugs (admin). Updates DB; reload list after.
 * @returns {Promise<Object>} e.g. { updated, workItemsModified, productionBugsModified }
 */
export const fetchProductionBugCodeFixes = async () => {
  const response = await authPost('/api/workitems/production-bugs/fetch-code-fixes', {}, { timeout: 600000 });
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to fetch Code Fix for production bugs');
};

/**
 * List rows from escalated-issues collection (newest first).
 * @param {Object} [params] - e.g. { limit: 500 }
 * @returns {Promise<Array>}
 */
export const getEscalatedIssues = async (params = {}) => {
  const response = await get('/api/workitems/escalated-issues', params);
  if (response && response.success && Array.isArray(response.data)) {
    return response.data;
  }
  return [];
};

/**
 * Query ADO for Investigations by Zendesk Support ID and linked Bug/PBI work items; upserts escalated-issues.
 * @param {string} userEnteredId
 * @returns {Promise<Object>}
 */
export const lookupEscalatedIssue = async (userEnteredId) => {
  const response = await post('/api/workitems/escalated-issues/lookup', {
    userEnteredId: String(userEnteredId || '').trim()
  });
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || 'Lookup failed');
};

/**
 * Delete one escalated-issues row (Mongo document id). Requires admin auth.
 * @param {string} id - EscalatedIssue _id
 * @returns {Promise<{ userEnteredId: string }>}
 */
export const deleteEscalatedIssue = async (id) => {
  const url = `/api/workitems/escalated-issues/${encodeURIComponent(String(id).trim())}`;
  const response = await authDelete(url);
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to delete escalated issue');
};

/**
 * Set admin-only mitigated flag on an escalated-issues row. Requires admin auth.
 * @param {string} id - EscalatedIssue Mongo _id
 * @param {boolean} adminMitigated
 * @returns {Promise<Object>} Updated document
 */
export const patchEscalatedIssueAdminMitigated = async (id, adminMitigated) => {
  const url = `/api/workitems/escalated-issues/${encodeURIComponent(String(id).trim())}`;
  const response = await authPatch(url, { adminMitigated: !!adminMitigated });
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to update escalated issue');
};

/**
 * Persist manual Escalated Issues table order (shared).
 * @param {string[]} orderedUserEnteredIds - L1 ticket ids in display order
 */
export const setEscalatedIssuesOrder = async (orderedUserEnteredIds) => {
  const response = await post('/api/workitems/escalated-issues/order', {
    orderedUserEnteredIds: Array.isArray(orderedUserEnteredIds) ? orderedUserEnteredIds : []
  });
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to save row order');
};

/**
 * Run enhanced bug code analysis for an ADO work item ID. Returns markdown report from Python agent.
 * @param {string} adoId - Azure DevOps work item ID (bug)
 * @param {string} [project] - Optional project name (default from env or "HighQ Tech")
 * @returns {Promise<{ markdown: string }>}
 */
export const fetchBugCodeAnalysis = async (adoId, project) => {
  const url = '/api/workitems/bug-code-analysis';
  const data = { bugId: String(adoId || '').trim() };
  if (project && String(project).trim()) data.project = String(project).trim();
  const response = await authPost(url, data, { timeout: 300000 }); // 5 minutes for complex analysis
  if (response && response.success && response.data && typeof response.data.markdown === 'string') {
    return {
      markdown: response.data.markdown,
      scanLog: response.data.scanLog || null,
      lastRefreshAt: response.data.lastRefreshAt || null,
      adoId: response.data.adoId || String(adoId || '').trim()
    };
  }
  throw new Error(response?.message || 'Failed to run bug code analysis');
};

/**
 * Get bug code analysis history
 * @param {Object} options - Query options (limit, offset)
 * @returns {Promise<{ data: Array, total: number }>}
 */
export const fetchBugCodeAnalysisHistory = async (options = {}) => {
  const { limit = 20, offset = 0 } = options;
  const url = `/api/workitems/bug-code-analysis?limit=${limit}&offset=${offset}`;
  const response = await authGet(url);
  if (response && response.success && Array.isArray(response.data)) {
    return {
      data: response.data,
      total: response.total || 0,
      limit: response.limit || limit,
      offset: response.offset || offset
    };
  }
  throw new Error(response?.message || 'Failed to fetch analysis history');
};

/**
 * Get specific bug code analysis by ID
 * @param {string} id - Analysis record ID
 * @returns {Promise<Object>}
 */
export const fetchBugCodeAnalysisById = async (id) => {
  const url = `/api/workitems/bug-code-analysis/${id}`;
  const response = await authGet(url);
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to fetch analysis');
};

/**
 * Delete bug code analysis record
 * @param {string} id - Analysis record ID
 * @returns {Promise<Object>}
 */
export const deleteBugCodeAnalysis = async (id) => {
  const url = `/api/workitems/bug-code-analysis/${id}`;
  const response = await authDelete(url);
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to delete analysis');
};

/**
 * Sync work item from ADO for an adoId, update workitems table and BugCodeAnalysis records with latest state.
 * @param {string} adoId - Azure DevOps work item ID
 * @returns {Promise<{ adoId: string, adoBugState?: string, adoCreatedDate?: string, updatedCount: number }>}
 */
export const syncBugCodeAnalysisFromAdo = async (adoId) => {
  const url = `/api/workitems/bug-code-analysis/sync-ado/${encodeURIComponent(String(adoId).trim())}`;
  const response = await authPost(url, {});
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || 'Failed to sync from ADO');
};

/**
 * Get persisted release internal bugs (ADO query URL and last synced columns/rows from release-internal-bugs collection)
 * @returns {Promise<{ queryUrl: string, syncedAt: string|null, columns: string[], rows: Array<Object> }>}
 */
export const getReleaseInternalBugs = async () => {
  const response = await get('/api/workitems/release-internal-bugs');
  if (response && response.success && response.data) {
    return response.data;
  }
  return { queryUrl: '', syncedAt: null, columns: [], rows: [] };
};

/**
 * Get all teams
 * @returns {Promise<Object>} - Teams data
 * @throws {Error} - If the request fails
 */
export const getTeams = async () => {
  console.log('Getting teams from API');
  return authGet('/api/admin/teams');
};

/**
 * Get enabled teams only (public endpoint)
 * @returns {Promise<Array>} - Array of enabled teams
 * @throws {Error} - If the request fails
 */
export const getEnabledTeams = async () => {
  console.log('Getting enabled teams from API (public endpoint)');
  const response = await get('/api/admin/teams/enabled');
  if (response.success && response.data) {
    return response.data;
  }
  return [];
};

/**
 * Get all teams with stream information (for WSR and other views)
 * @returns {Promise<Array>} - Teams with stream info
 * @throws {Error} - If the request fails
 */
export const getTeamsWithStreams = async () => {
  console.log('Getting teams with streams from API (public endpoint)');
  const response = await get('/api/admin/teams/streams');
  if (response.success && response.data) {
    return response.data;
  }
  return [];
};

/**
 * Upload CSV file for TSI tickets (Admin only)
 * @param {File} file - CSV file to upload
 * @returns {Promise<Object>} - Upload result
 * @throws {Error} - If the request fails
 */
export const uploadTsiTicketsCsv = async (file) => {
  try {
    console.log('Uploading TSI tickets CSV file:', file.name);
    
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/tsi-tickets/upload`;
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('csvFile', file);
    
    // Make the request
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    console.log('TSI CSV upload response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      
      // Check for session timeout
      if (errorData.error === 'session_timeout') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
        throw new Error('Your session has timed out. Please log in again.');
      }
      
      throw new Error(errorData.message || `Upload failed with status: ${response.status}`);
    }
    
    const responseData = await response.json();
    console.log('TSI CSV upload successful');

    return responseData;

  } catch (error) {
    console.error('Error uploading TSI tickets CSV:', error);

    if (error.message.includes('Authentication required')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    throw error;
  }
};

/**
 * Fetch TSI tickets from ADO using a query (saved-query GUID or WIQL).
 * Clears existing tickets and repopulates from the results (Admin only).
 * @param {string} query - ADO saved-query GUID or a raw WIQL string
 */
export const fetchTsiTicketsFromAdo = async (query) => {
  try {
    console.log('Fetching TSI tickets from ADO');

    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/tsi-tickets/fetch-from-ado`;

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.error === 'session_timeout') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
        throw new Error('Your session has timed out. Please log in again.');
      }
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }

    const responseData = await response.json();
    console.log('TSI ADO fetch successful');
    return responseData;

  } catch (error) {
    console.error('Error fetching TSI tickets from ADO:', error);
    if (error.message.includes('Authentication required')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

/**
 * Get TSI tickets with optional filtering and pagination
 * @param {Object} filters - Optional filters to apply
 * @param {Object} pagination - Optional pagination parameters
 * @returns {Promise<Object>} - TSI tickets data
 * @throws {Error} - If the request fails
 */
export const getTsiTickets = async (filters = {}, pagination = {}) => {
  console.log('Getting TSI tickets from API with filters:', filters, 'pagination:', pagination);
  
  // Build query string from filters and pagination
  const queryParams = new URLSearchParams();
  
  // Add filters to query params (excluding workItemType, handling arrays properly)
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Skip workItemType filter as it's been removed
      if (key !== 'workItemType') {
        // Handle arrays (for multiselect filters like state and environment)
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== undefined && item !== null && item !== '') {
              queryParams.append(key, item);
            }
          });
        } else {
          queryParams.append(key, value);
        }
      }
    }
  });
  
  // Add pagination to query params
  Object.entries(pagination).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/tsi-tickets${queryString ? `?${queryString}` : ''}`;
  
  console.log('TSI API Service - Final URL:', url);
  
  return get(url);
};

/**
 * Get ADO email groups configuration
 * @returns {Promise<Object>} - ADO email groups configuration
 * @throws {Error} - If the request fails
 */
export const getAdoEmailGroups = async () => {
  console.log('Getting ADO email groups from API');
  return authGet('/api/admin/ado-email-groups');
};

/**
 * Admin: get the TSI teams config + assignee lists (for the group editor).
 * @returns {Promise<Object>} - { success, data: { groups, allAssignees, unassigned } }
 */
export const getTsiTeamsAdmin = async () => {
  return authGet('/api/admin/tsi-teams');
};

/**
 * Admin: save the TSI teams config.
 * @param {Array} groups - [{ id, name, members: string[] }]
 * @returns {Promise<Object>} - { success, data: { groups } }
 */
export const saveTsiTeamsAdmin = async (groups) => {
  return authPut('/api/admin/tsi-teams', { groups });
};

/**
 * Read the TSI assignee-group configuration for the pivot table (public read).
 * @returns {Promise<Object>} - { success, data: { groups } }
 */
export const getTsiTeamConfig = async () => {
  return get('/api/tsi-tickets/team-config');
};

/**
 * Get TSI ticket statistics
 * @returns {Promise<Object>} - TSI ticket statistics
 * @throws {Error} - If the request fails
 */
export const getTsiTicketStats = async () => {
  console.log('Getting TSI ticket statistics from API');
  return get('/api/tsi-tickets/stats');
};

/**
 * Get available columns from TSI tickets dataset
 * @returns {Promise<Object>} - Available columns
 * @throws {Error} - If the request fails
 */
export const getTsiTicketColumns = async () => {
  console.log('Getting TSI ticket columns from API');
  return get('/api/tsi-tickets/columns');
};

/**
 * Clear all TSI tickets (Admin only)
 * @returns {Promise<Object>} - Clear result
 * @throws {Error} - If the request fails
 */
export const clearTsiTickets = async () => {
  console.log('Clearing all TSI tickets');
  return authDelete('/api/tsi-tickets/clear');
};

/**
 * Get open vs closed tickets by month from July 2024
 * @returns {Promise<Object>} - Monthly open/closed data
 * @throws {Error} - If the request fails
 */
export const getTsiOpenVsClosedChart = async () => {
  console.log('Getting TSI open vs closed chart data from API');
  return get('/api/tsi-tickets/charts/open-vs-closed');
};

/**
 * Get closed tickets by month grouped by Investigation Classification
 * @returns {Promise<Object>} - Monthly closed tickets by classification
 * @throws {Error} - If the request fails
 */
export const getTsiClosedByClassificationChart = async () => {
  console.log('Getting TSI closed by classification chart data from API');
  return get('/api/tsi-tickets/charts/closed-by-classification');
};

/**
 * Get weekly incoming tickets by priority for last 10 weeks
 * @returns {Promise<Object>} - Weekly incoming tickets by priority
 * @throws {Error} - If the request fails
 */
export const getTsiWeeklyIncomingByPriorityChart = async () => {
  console.log('Getting TSI weekly incoming by priority chart data from API');
  return get('/api/tsi-tickets/charts/weekly-incoming-by-priority');
};

/**
 * Get non-closed tickets by month of creation, stacked by priority
 * @returns {Promise<Object>} - Monthly non-closed tickets by priority
 * @throws {Error} - If the request fails
 */
export const getTsiNonClosedByPriorityChart = async () => {
  console.log('Getting TSI non-closed by priority chart data from API');
  return get('/api/tsi-tickets/charts/non-closed-by-priority');
};

/**
 * Get daily Created vs Closed counts over the last N days, optionally filtered by assignee.
 * @param {Object} [opts]
 * @param {number} [opts.days=60]
 * @param {string[]} [opts.assignees] - restrict to these assignees
 * @returns {Promise<Object>} - { success, data: [{ date, created, closed }] }
 */
export const getTsiDailyCreatedVsClosedChart = async ({ days = 60, assignees = [] } = {}) => {
  const params = new URLSearchParams();
  if (days) params.set('days', String(days));
  if (assignees && assignees.length) params.set('assignees', assignees.join(','));
  const qs = params.toString();
  return get(`/api/tsi-tickets/charts/daily-created-vs-closed${qs ? `?${qs}` : ''}`);
};

/**
 * Get the distinct assignees across TSI tickets (for the daily-chart assignee filter).
 * @returns {Promise<Object>} - { success, data: string[] }
 */
export const getTsiAssignees = async () => {
  return get('/api/tsi-tickets/assignees');
};

/**
 * Get average age of closed TSI tickets by created month for last 12 months
 * @returns {Promise<Object>} - Monthly average age by created month
 * @throws {Error} - If the request fails
 */
export const getTsiAverageAgeByCreatedMonth = async () => {
  console.log('Getting TSI average age by created month chart data from API');
  return get('/api/tsi-tickets/charts/average-age-by-created-month');
};

/**
 * Get average age of closed TSI tickets by closed month for last 12 months
 * @returns {Promise<Object>} - Monthly average age by closed month
 * @throws {Error} - If the request fails
 */
export const getTsiAverageAgeByClosedMonth = async () => {
  console.log('Getting TSI average age by closed month chart data from API');
  return get('/api/tsi-tickets/charts/average-age-by-closed-month');
};

/**
 * Get weekly incoming tickets by environment for last 10 weeks
 * @returns {Promise<Object>} - Weekly incoming tickets by environment
 * @throws {Error} - If the request fails
 */
export const getTsiWeeklyIncomingByEnvironmentChart = async () => {
  console.log('Getting TSI weekly incoming by environment chart data from API');
  return get('/api/tsi-tickets/charts/weekly-incoming-by-environment');
};

/**
 * Get weekly incoming tickets by investigation classification for last 10 weeks
 * @returns {Promise<Object>} - Weekly incoming tickets by classification
 * @throws {Error} - If the request fails
 */
export const getTsiWeeklyIncomingByClassificationChart = async () => {
  console.log('Getting TSI weekly incoming by classification chart data from API');
  return get('/api/tsi-tickets/charts/weekly-incoming-by-classification');
};

/**
 * Get all fix versions
 * @returns {Promise<Object>} - Fix versions data
 * @throws {Error} - If the request fails
 */
export const getAllFixVersions = async () => {
  console.log('Getting all fix versions from API');
  return get('/api/fix-versions');
};

/**
 * Get fix versions with optional filters
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Object>} - Fix versions data
 * @throws {Error} - If the request fails
 */
export const getFixVersions = async (filters = {}) => {
  console.log('Getting fix versions from API with filters:', filters);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString();
  const url = `/api/fix-versions${queryString ? `?${queryString}` : ''}`;
  
  return authGet(url);
};

/**
 * Get a single fix version by ID
 * @param {String} id - Fix version ID
 * @returns {Promise<Object>} - Fix version data
 * @throws {Error} - If the request fails
 */
export const getFixVersion = async (id) => {
  console.log(`Getting fix version with ID: ${id}`);
  return authGet(`/api/fix-versions/${id}`);
};

/**
 * Create a new fix version
 * @param {Object} data - Fix version data
 * @returns {Promise<Object>} - Created fix version data
 * @throws {Error} - If the request fails
 */
export const createFixVersion = async (data) => {
  console.log('Creating new fix version with data:', data);
  return authPost('/api/fix-versions', data);
};

/**
 * Update an existing fix version
 * @param {String} version - Fix version string (e.g., "5.9.14") - used instead of _id for stability
 * @param {Object} data - Updated fix version data
 * @returns {Promise<Object>} - Updated fix version data
 * @throws {Error} - If the request fails
 */
export const updateFixVersion = async (version, data) => {
  console.log(`Updating fix version with version: ${version}`, data);
  // URL encode the version to handle any special characters
  const encodedVersion = encodeURIComponent(version);
  return authPut(`/api/fix-versions/${encodedVersion}`, data);
};

/**
 * Get TSI tickets by classification and month
 * @param {String} classification - Classification to filter by
 * @param {String} month - Month to filter by (YYYY-MM format)
 * @returns {Promise<Object>} - TSI tickets data
 * @throws {Error} - If the request fails
 */
export const getTsiTicketsByClassificationAndMonth = async (classification, month) => {
  console.log(`Getting TSI tickets for classification: ${classification} and month: ${month}`);
  return get(`/api/tsi-tickets/classification/${classification}/month/${month}`);
};

/**
 * Get TSI tickets by assignee and month
 * @param {String} assignee - Assignee to filter by
 * @param {String} month - Month to filter by (YYYY-MM format)
 * @returns {Promise<Object>} - TSI tickets data
 * @throws {Error} - If the request fails
 */
export const getTsiTicketsByAssigneeAndMonth = async (assignee, month) => {
  console.log(`Getting TSI tickets for assignee: ${assignee} and month: ${month}`);
  return get(`/api/tsi-tickets/assignee/${assignee}/month/${month}`);
};

/**
 * Get assignee pivot table data - tickets grouped by assignee and month
 * @param {Date} startDate - Optional start date for the pivot table
 * @param {Date} endDate - Optional end date for the pivot table
 * @returns {Promise<Object>} - Pivot table data
 * @throws {Error} - If the request fails
 */
export const getTsiAssigneePivotTable = async (startDate = null, endDate = null) => {
  console.log('Getting TSI assignee pivot table data');
  
  // Build query parameters if dates are provided
  const params = {};
  if (startDate) {
    params.startDate = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }
  if (endDate) {
    params.endDate = endDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }
  
  return get('/api/tsi-tickets/pivot/assignee', params);
};

/**
 * Get linked bugs for all TSI tickets from ADO (Admin only)
 * @returns {Promise<Object>} - Fetch result with stats
 * @throws {Error} - If the request fails
 */
export const getLinkedBugsForTsiTickets = async () => {
  try {
    console.log('Fetching linked bugs for all TSI tickets');
    
    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/tsi-tickets/get-linked-bugs`;
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      if (errorData.error === 'session_timeout') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
        throw new Error('Your session has timed out. Please log in again.');
      }
      
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }
    
    const responseData = await response.json();
    console.log('Linked bugs fetch successful');
    
    return responseData;
    
  } catch (error) {
    console.error('Error fetching linked bugs for TSI tickets:', error);
    
    if (error.message.includes('Authentication required')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }
};

/**
 * Get linked bugs for a specific TSI ticket
 * @param {String} ticketId - TSI ticket ID
 * @returns {Promise<Object>} - Linked bugs data
 * @throws {Error} - If the request fails
 */
export const getTsiTicketLinks = async (ticketId) => {
  try {
    console.log(`Getting linked bugs for ticket ${ticketId}`);
    
    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/tsi-tickets/${ticketId}/links`;

    const token = localStorage.getItem('token');
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }
    
    const responseData = await response.json();
    return responseData;
    
  } catch (error) {
    console.error(`Error getting linked bugs for ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Make an authenticated POST request with FormData (for file uploads)
 * @param {String} url - URL to fetch
 * @param {FormData} formData - FormData to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const authPostFormData = async (url, formData, options = {}) => {
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();
    
    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    
    console.log('Making authenticated POST request with FormData to:', fullUrl);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for POST request to:', fullUrl);
      throw new Error('Authentication required');
    }
    
    // Make the request
    console.log('Sending POST request with auth token and FormData');
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type for FormData - let browser set it with boundary
      },
      body: formData
    });
    
    console.log('POST FormData response status:', response.status, 'for URL:', url);
    
    // Check if response is ok
    if (!response.ok) {
      console.error('POST FormData response not OK:', response.status, response.statusText, 'for URL:', url);
      
      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from POST FormData request:', errorData);
        
        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          console.log('Session timeout detected, redirecting to login');
          // Clear localStorage and redirect to login with timeout flag
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Parse response
    const responseData = await response.json();
    console.log('POST FormData request successful for URL:', url);
    
    // Invalidate related cache entries if specified
    if (options.invalidateCache) {
      if (Array.isArray(options.invalidateCache)) {
        options.invalidateCache.forEach(pattern => invalidateCache(pattern));
      } else {
        invalidateCache(options.invalidateCache);
      }
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in authPostFormData:', error, 'for URL:', url);
    
    // Handle specific error cases
    if (error.message.includes('Authentication required')) {
      console.error('Authentication error in POST FormData request');
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw error;
  }
};

/**
 * Convenience method for POST requests (alias for authPost)
 * @param {String} url - URL to fetch
 * @param {Object} data - Data to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
/**
 * Create AbortController with timeout for requests
 * @param {Number} timeoutMs - Timeout in milliseconds
 * @returns {Object} - AbortController and timeout ID
 */
const createAbortController = (timeoutMs) => {
  if (!timeoutMs) {
    // No timeout - return a controller without timeout
    return { controller: new AbortController(), timeoutId: null };
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, timeoutId };
};

/**
 * Make a simple POST request without authentication
 * @param {String} url - URL to fetch
 * @param {Object} data - Data to send
 * @param {Object} options - Additional options (timeout in milliseconds)
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const post = async (url, data, options = {}) => {
  let timeoutId;
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();

    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;

    console.log('Making POST request to:', fullUrl);
    console.log('POST request data:', data);

    // Create AbortController for timeout support (only if timeout is specified)
    // No default timeout - allows long-running operations to complete
    const timeout = options.timeout; // Only use timeout if explicitly provided
    let controller = null;
    if (timeout) {
      const { controller: ctrl, timeoutId: tid } = createAbortController(timeout);
      controller = ctrl;
      timeoutId = tid;
    }

    // Make the request
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
      signal: controller?.signal
    });

    console.log('POST response status:', response.status, 'for URL:', url);

    // Check if response is ok
    if (!response.ok) {
      console.error('POST response not OK:', response.status, response.statusText, 'for URL:', url);

      // Try to parse error message from JSON
      try {
        const errorData = await response.json();
        console.error('Error data from POST request:', errorData);
        throw new Error(errorData.message || `Request failed with status: ${response.status}`);
      } catch (jsonError) {
        console.error('Error parsing error response JSON:', jsonError);
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
    }

    // Parse response
    const responseData = await response.json();
    console.log('POST request successful for URL:', url);

    return responseData;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('POST request timed out for URL:', url);
      throw new Error(`Request timed out after ${options.timeout || 'unknown'}ms`);
    }
    console.error('Error in post:', error, 'for URL:', url);
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

/**
 * Make a simple DELETE request without authentication
 * @param {String} url - URL to fetch
 * @param {Object} options - Additional options (timeout in milliseconds)
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If the request fails
 */
export const deleteRequest = async (url, options = {}) => {
  let timeoutId;
  try {
    // Ensure we're using the most up-to-date API URL
    const baseUrl = await getCurrentBaseUrl();

    // If the URL is already absolute, use it as is
    const fullUrl = url.startsWith('http') ? url : url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;

    console.log('Making DELETE request to:', fullUrl);

    // Create AbortController for timeout support (only if timeout is specified)
    // No default timeout - allows long-running operations to complete
    const timeout = options.timeout; // Only use timeout if explicitly provided
    let controller = null;
    if (timeout) {
      const { controller: ctrl, timeoutId: tid } = createAbortController(timeout);
      controller = ctrl;
      timeoutId = tid;
    }

    // Make the request
    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: controller?.signal
    });

    console.log('DELETE response status:', response.status, 'for URL:', url);

    // Check if response is ok
    if (!response.ok) {
      const errorText = await response.text();
      console.error('DELETE request failed:', response.status, errorText, 'for URL:', url);
      throw new Error(`DELETE failed with status ${response.status}: ${errorText}`);
    }

    // Parse response
    const responseData = await response.json();
    console.log('DELETE request successful for URL:', url);

    return responseData;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('DELETE request timed out for URL:', url);
      throw new Error(`Request timed out after ${options.timeout || 'unknown'}ms`);
    }
    console.error('Error in deleteRequest:', error, 'for URL:', url);
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

/**
 * Get code changes for a pull request.
 * Uses public GET so Files Changed is visible without login (backend route is public).
 * @param {Number} prNumber - Pull request number
 * @param {String} appId - Application ID
 * @returns {Promise<Object>} Code changes data
 */
export const getPRCodeChanges = async (prNumber, appId) => {
  try {
    const url = `/api/pull-requests/${prNumber}/code-changes`;
    const params = { appId };
    return await get(url, params);
  } catch (error) {
    console.error('Error fetching PR code changes:', error);
    throw error;
  }
};

/**
 * Sync test definitions from Drill4J PostgreSQL to MongoDB
 * @returns {Promise<Object>} Sync result
 */
export const syncDrill4JTests = async () => {
  try {
    const baseUrl = await getCurrentBaseUrl();
    const fullUrl = `${baseUrl}/api/drill4j/sync`;
    
    console.log('Making POST request to sync Drill4J tests:', fullUrl);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed with status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error syncing Drill4J tests:', error);
    throw error;
  }
};

/**
 * Get matched tests with registration status
 * @param {Object} filters - Filter parameters (adoId, component, subComponent)
 * @returns {Promise<Object>} Matched tests data
 */
export const getDrill4JTests = async (filters = {}) => {
  try {
    const url = '/api/drill4j/tests';
    return await get(url, filters);
  } catch (error) {
    console.error('Error fetching Drill4J tests:', error);
    throw error;
  }
};

/**
 * Get tests by discrepancy type (unmatched, deleted, other)
 * @param {string} type - Discrepancy type: 'unmatched', 'deleted', or 'other'
 * @returns {Promise<Object>} Tests data
 */
export const getDrill4JTestsByDiscrepancyType = async (type) => {
  try {
    const url = `/api/drill4j/discrepancy/${type}`;
    return await get(url);
  } catch (error) {
    console.error(`Error fetching Drill4J tests by discrepancy type ${type}:`, error);
    throw error;
  }
};

/**
 * Get tests not registered in Drill4J, grouped by testSuite and automationStatus
 * @returns {Promise<Object>} Tests data grouped by suite and status
 */
export const getTestsNotInDrill4J = async () => {
  try {
    const url = '/api/drill4j/not-in-drill4j';
    return await get(url);
  } catch (error) {
    console.error('Error fetching tests not in Drill4J:', error);
    throw error;
  }
};

// ----- Coverage (Drill4J tree) API methods -----

export const getCoverageBuilds = async (params = {}) => {
  return await get('/api/coverage/builds', params);
};

export const getCoverageOverview = async (params = {}) => {
  return await get('/api/coverage/overview', params);
};

export const getCoveragePackages = async (params = {}) => {
  return await get('/api/coverage/packages', params);
};

export const getCoverageClasses = async (params = {}) => {
  return await get('/api/coverage/classes', params);
};

export const getCoverageMethods = async (params = {}) => {
  return await get('/api/coverage/methods', params);
};

export const getCoverageTestsForMethod = async (params = {}) => {
  return await get('/api/coverage/tests-for-method', params);
};

export const searchCoverage = async (params = {}) => {
  return await get('/api/coverage/search', params);
};

export const getCoverageModuleStructure = async () => {
  return await get('/api/coverage/module-structure');
};

/**
 * Get ServiceNow configuration (Admin only)
 * @returns {Promise<Object>} - ServiceNow configuration
 * @throws {Error} - If the request fails
 */
export const getServiceNowConfig = async () => {
  return authGet('/api/servicenow/config');
};

/**
 * Update ServiceNow configuration (Admin only)
 * @param {Object} config - Configuration object
 * @returns {Promise<Object>} - Updated configuration
 * @throws {Error} - If the request fails
 */
export const updateServiceNowConfig = async (config) => {
  return authPut('/api/servicenow/config', config);
};

/**
 * Test ServiceNow connection (Admin only)
 * @returns {Promise<Object>} - Connection test result
 * @throws {Error} - If the request fails
 */
export const testServiceNowConnection = async () => {
  return authPost('/api/servicenow/test-connection');
};

/**
 * Get incidents from ServiceNow
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Incidents data
 * @throws {Error} - If the request fails
 */
export const getServiceNowIncidents = async (options = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const queryString = queryParams.toString();
  return authGet(`/api/servicenow/incidents${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get synced ServiceNow tickets from database
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} - Tickets data
 * @throws {Error} - If the request fails
 */
export const getServiceNowTickets = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const queryString = queryParams.toString();
  return authGet(`/api/servicenow/tickets${queryString ? `?${queryString}` : ''}`);
};

/**
 * Download the Incident Report attachment (filename like "Incident Report - &lt;id&gt;.document") from ServiceNow.
 * @param {string} ticketSysId - ServiceNow incident sys_id (stored as ticket.sysId)
 * @param {{ refresh?: boolean }} [options] - refresh=true re-resolves attachment from ServiceNow
 * @returns {Promise<Blob>}
 */
export const downloadServiceNowIncidentReport = async (ticketSysId, options = {}) => {
  const q = options.refresh ? '?refresh=1' : '';
  return authGet(
    `/api/servicenow/tickets/${encodeURIComponent(ticketSysId)}/incident-report${q}`,
    null,
    'blob',
    { useCache: false }
  );
};

/**
 * Sync incidents from ServiceNow to database (Admin only)
 * @param {Object} options - Sync options
 * @returns {Promise<Object>} - Sync result
 * @throws {Error} - If the request fails
 */
export const syncServiceNowIncidents = async (options = {}) => {
  return authPost('/api/servicenow/sync', options);
};

/**
 * Sync alerts from ServiceNow to database (Admin only)
 * @param {Object} options - Sync options
 * @returns {Promise<Object>} - Sync result
 * @throws {Error} - If the request fails
 */
export const syncServiceNowAlerts = async (options = {}) => {
  return authPost('/api/servicenow/sync-alerts', options);
};

/**
 * Reset incidents table - delete all incidents (Admin only)
 * @returns {Promise<Object>} - Reset result
 * @throws {Error} - If the request fails
 */
export const resetServiceNowIncidents = async () => {
  return authDelete('/api/servicenow/incidents/reset');
};

/**
 * Reset alerts table - delete all alerts (Admin only)
 * @returns {Promise<Object>} - Reset result
 * @throws {Error} - If the request fails
 */
export const resetServiceNowAlerts = async () => {
  return authDelete('/api/servicenow/alerts/reset');
};

/**
 * Get ServiceNow ticket statistics
 * @returns {Promise<Object>} - Ticket statistics
 * @throws {Error} - If the request fails
 */
export const getServiceNowTicketStats = async () => {
  return authGet('/api/servicenow/tickets/stats');
};

/**
 * Get ServiceNow alerts from database
 * @param {Object} filter - Filter options (page, limit, state, severity, category, assignmentGroup, search)
 * @returns {Promise<Object>} - Alerts data with pagination
 * @throws {Error} - If the request fails
 */
export const getServiceNowAlerts = async (filter = {}) => {
  const params = new URLSearchParams();
  Object.keys(filter).forEach(key => {
    if (filter[key] !== undefined && filter[key] !== null && filter[key] !== '') {
      params.append(key, filter[key]);
    }
  });
  const queryString = params.toString();
  return authGet(`/api/servicenow/alerts${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get ServiceNow alert statistics
 * @param {number} days - Number of days to look back (default: 30)
 * @returns {Promise<Object>} - Alert statistics
 * @throws {Error} - If the request fails
 */
export const getServiceNowAlertStats = async (days = 30) => {
  return authGet(`/api/servicenow/alerts/stats?days=${days}`);
};

/**
 * Get tickets per day trend by service/env/team
 * @param {number} days - Number of days to look back
 * @param {string} groupBy - Group by 'service', 'env', or 'team'
 * @returns {Promise<Object>} - Tickets per day data
 */
export const getTicketsPerDayTrend = async (days = 30, groupBy = 'service') => {
  return authGet(`/api/servicenow/alerts/metrics/tickets-per-day?days=${days}&groupBy=${groupBy}`);
};

/**
 * Get MTTA/MTTR metrics (median + p90)
 * @param {number} days - Number of days to look back
 * @returns {Promise<Object>} - MTTA/MTTR metrics
 */
export const getMTTAMTTRMetrics = async (days = 30) => {
  return authGet(`/api/servicenow/alerts/metrics/mtta-mttr?days=${days}`);
};

/**
 * Get top monitors generating tickets
 * @param {number} days - Number of days to look back
 * @param {number} limit - Number of top monitors to return (default: 10)
 * @returns {Promise<Object>} - Top monitors data
 */
export const getTopMonitors = async (days = 30, limit = 10) => {
  return authGet(`/api/servicenow/alerts/metrics/top-monitors?days=${days}&limit=${limit}`);
};

/**
 * Generate AI report for alerts using Claude
 * @param {Object} options - Report generation options
 * @returns {Promise<Object>} - Generated report
 */
export const generateAlertReport = async (options = {}) => {
  return authPost('/api/servicenow/alerts/generate-report', options);
};

/**
 * Get all test instances (Public)
 * @returns {Promise<Object>} - Test instances data
 * @throws {Error} - If the request fails
 */
export const getTestInstances = async () => {
  return get('/api/test-instances');
};

/**
 * Get test instance by ID (Public)
 * @param {String} id - Test instance ID
 * @returns {Promise<Object>} - Test instance data
 * @throws {Error} - If the request fails
 */
export const getTestInstanceById = async (id) => {
  return get(`/api/test-instances/${id}`);
};

/**
 * Create a new test instance (Admin only)
 * @param {Object} data - Test instance data (baseURL, contextName)
 * @returns {Promise<Object>} - Created test instance
 * @throws {Error} - If the request fails
 */
export const createTestInstance = async (data) => {
  return authPost('/api/test-instances', data);
};

/**
 * Update test instance (Admin only)
 * @param {String} id - Test instance ID
 * @param {Object} data - Updated test instance data
 * @returns {Promise<Object>} - Updated test instance
 * @throws {Error} - If the request fails
 */
export const updateTestInstance = async (id, data) => {
  return authPut(`/api/test-instances/${id}`, data);
};

/**
 * Delete test instance (Admin only)
 * @param {String} id - Test instance ID
 * @returns {Promise<Object>} - Deletion result
 * @throws {Error} - If the request fails
 */
export const deleteTestInstance = async (id) => {
  return authDelete(`/api/test-instances/${id}`);
};

/**
 * Fetch CBV data for a test instance (Public)
 * @param {String} id - Test instance ID
 * @returns {Promise<Object>} - CBV data
 * @throws {Error} - If the request fails
 */
export const fetchTestInstanceCBV = async (id) => {
  return post(`/api/test-instances/${id}/fetch-cbv`, {});
};

/**
 * Fetch MSV data for a test instance (Public)
 * @param {String} id - Test instance ID
 * @returns {Promise<Object>} - MSV data
 * @throws {Error} - If the request fails
 */
export const fetchTestInstanceMSV = async (id) => {
  return post(`/api/test-instances/${id}/fetch-msv`, {});
};

/**
 * Fetch Azure data for a test instance (Public, placeholder)
 * @param {String} id - Test instance ID
 * @returns {Promise<Object>} - Azure data
 * @throws {Error} - If the request fails
 */
export const fetchTestInstanceAzure = async (id) => {
  return post(`/api/test-instances/${id}/fetch-azure`, {});
};

// Instance Management API methods (namespaces + services tree, getCBV)
export const getInstanceManagementNamespaces = async () => {
  const res = await get('/api/instance-management/namespaces');
  return res && res.data !== undefined ? res : { data: res };
};

export const createInstanceManagementNamespace = async (data) => {
  return authPost('/api/instance-management/namespaces', data);
};

export const updateInstanceManagementNamespace = async (id, data) => {
  return authPut(`/api/instance-management/namespaces/${id}`, data);
};

export const deleteInstanceManagementNamespace = async (id) => {
  return authDelete(`/api/instance-management/namespaces/${id}`);
};

export const createInstanceManagementService = async (namespaceId, data) => {
  return authPost(`/api/instance-management/namespaces/${namespaceId}/services`, data);
};

export const updateInstanceManagementService = async (id, data) => {
  return authPut(`/api/instance-management/services/${id}`, data);
};

export const deleteInstanceManagementService = async (id) => {
  return authDelete(`/api/instance-management/services/${id}`);
};

export const fetchInstanceManagementServiceCBV = async (serviceId) => {
  return post(`/api/instance-management/services/${serviceId}/fetch-cbv`, {});
};

/**
 * Export instance management data as JSON (namespaces + services with githubRepoName).
 */
export const exportInstanceManagementData = async () => {
  return authGet('/api/instance-management/export');
};

/**
 * Import instance management data from JSON (merge: create or update by namespace name + baseURL).
 */
export const importInstanceManagementData = async (data) => {
  return authPost('/api/instance-management/import', data);
};

/**
 * Get test plan generation logs for a work item
 * @param {String} adoId - ADO work item ID
 * @returns {Promise<Object>} Test plan logs
 * @throws {Error} If the request fails
 */
export const getTestPlanLogs = async (adoId) => {
  return get(`/api/test-plans/work-item/${adoId}/logs`);
};

// ── CI Test Plan (GitHub Actions "Test Planning" workflow) ───────────────────

/** Fetch work item details (Title, Type, State, Team, descendants) from the DB. */
export const fetchTestPlanWorkItem = async (adoId) => get(`/api/test-plans/work-item/${adoId}`);

/** Request a test plan: stores a persistent row (status running) and dispatches the workflow. */
export const generateCiTestPlan = async (adoId, details = {}) =>
  authPost(`/api/test-plans/work-item/${adoId}/generate-ci`, details);

/** List all test plan runs (the Test Plans table); reconciles running rows server-side. */
export const getCiRuns = async () => get('/api/test-plans/ci-runs');

/** Absolute URL to a stored CI test-plan HTML report (opens in a new tab). */
export const getCiTestPlanUrl = (adoId, version) => {
  const base = (typeof window !== 'undefined' && window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.API_URL) || '';
  return `${base}/api/test-plans/work-item/${adoId}/ci-plan/${version}`;
};

// Default export will be at the end of the file after all functions are defined

/**
 * Get activity statistics (Admin only)
 * @returns {Promise<Object>} Activity statistics
 * @throws {Error} If the request fails
 */
export const getActivityStats = async () => {
  return authGet('/api/admin/activity/stats');
};

/**
 * Get active sessions count (Public endpoint)
 * @returns {Promise<Object>} Active sessions count
 * @throws {Error} If the request fails
 */
export const getActiveSessionsCount = async () => {
  return get('/api/activity/sessions-count');
};

/**
 * Get active sessions details (Public endpoint)
 * @returns {Promise<Object>} Active sessions details with IP, user, activity info
 * @throws {Error} If the request fails
 */
export const getActiveSessions = async () => {
  return get('/api/activity/sessions');
};

/**
 * Test Scenarios API Methods
 */

/**
 * Get scenarios for a test plan ticket
 * @param {String} ticketId - ADO ticket ID
 * @param {Boolean} includeIrrelevant - Whether to include irrelevant scenarios
 * @returns {Promise<Object>} Scenarios data
 */
export const getScenarios = async (ticketId, includeIrrelevant = false) => {
  return get(`/api/test-scenarios/ticket/${ticketId}`, { 
    includeIrrelevant: includeIrrelevant ? 'true' : 'false' 
  });
};

/**
 * Get single scenario by ID
 * @param {String} scenarioId - Scenario ID
 * @returns {Promise<Object>} Scenario data
 */
export const getScenario = async (scenarioId) => {
  return get(`/api/test-scenarios/${scenarioId}`);
};

/**
 * Mark scenario as irrelevant
 * @param {String} scenarioId - Scenario ID
 * @returns {Promise<Object>} Updated scenario
 */
export const markScenarioIrrelevant = async (scenarioId) => {
  return authPost(`/api/test-scenarios/${scenarioId}/mark-irrelevant`);
};

/**
 * Mark scenario as relevant
 * @param {String} scenarioId - Scenario ID
 * @returns {Promise<Object>} Updated scenario
 */
export const markScenarioRelevant = async (scenarioId) => {
  return authPost(`/api/test-scenarios/${scenarioId}/mark-relevant`);
};

/**
 * Create ADO test case for scenario
 * @param {String} scenarioId - Scenario ID
 * @param {String} component - Component value (optional)
 * @returns {Promise<Object>} Created test case data
 */
export const createAdoTestCase = async (scenarioId, component = '') => {
  return authPost(`/api/test-scenarios/${scenarioId}/create-ado-test`, { component });
};

/**
 * Link existing ADO test case to scenario
 * @param {String} scenarioId - Scenario ID
 * @param {String} adoTestId - ADO test case ID
 * @returns {Promise<Object>} Updated scenario
 */
export const linkAdoTestCase = async (scenarioId, adoTestId) => {
  return authPost(`/api/test-scenarios/${scenarioId}/link-ado-test`, { adoTestId });
};

/**
 * Unlink/remove ADO test case from scenario
 * @param {String} scenarioId - Scenario ID
 * @returns {Promise<Object>} Updated scenario
 */
export const unlinkAdoTestCase = async (scenarioId) => {
  return authPost(`/api/test-scenarios/${scenarioId}/unlink-ado-test`);
};

/**
 * Get Test Case type definition and mandatory fields
 * @returns {Promise<Object>} Test Case fields data
 */
export const getTestCaseFields = async () => {
  return get('/api/test-scenarios/test-case-fields');
};

/**
 * Refine test cases from scenarios using LLM optimization - DISABLED for offline work
 * @param {String} adoId - ADO ticket ID
 * @returns {Promise<Object>} Generated test cases
 */
/*
export const refineTestCases = async (adoId) => {
  return authPost(`/api/test-cases/${adoId}/refine`);
};
*/

/**
 * Get test cases for an ADO ticket
 * @param {String} adoId - ADO ticket ID
 * @returns {Promise<Object>} Test cases data
 */
export const getTestCases = async (adoId) => {
  return get(`/api/test-cases/${adoId}`);
};

/**
 * Get single test case by ID
 * @param {String} testCaseId - Test case ID
 * @returns {Promise<Object>} Test case data
 */
export const getTestCase = async (testCaseId) => {
  return get(`/api/test-cases/${testCaseId}`);
};

/**
 * Create ADO test case for test case
 * @param {String} testCaseId - Test case ID
 * @param {String} component - Component value (optional)
 * @returns {Promise<Object>} Created test case data
 */
export const createAdoTestCaseForTestCase = async (testCaseId, component = '') => {
  return authPost(`/api/test-cases/${testCaseId}/create-ado-test`, { component });
};

/**
 * Link existing ADO test case to test case
 * @param {String} testCaseId - Test case ID
 * @param {String} adoTestId - ADO test case ID
 * @returns {Promise<Object>} Updated test case
 */
export const linkAdoTestCaseForTestCase = async (testCaseId, adoTestId) => {
  return authPost(`/api/test-cases/${testCaseId}/link-ado-test`, { adoTestId });
};

/**
 * Unlink/remove ADO test case from test case
 * @param {String} testCaseId - Test case ID
 * @returns {Promise<Object>} Updated test case
 */
export const unlinkAdoTestCaseForTestCase = async (testCaseId) => {
  return authPost(`/api/test-cases/${testCaseId}/unlink-ado-test`);
};

/**
 * Delete test plan and related data for a work item (Public API - no authentication required)
 * @param {String} ticketId - Work item/ticket ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteTestPlan = async (ticketId) => {
  console.log('Deleting test plan for ticket:', ticketId);
  return deleteRequest(`/api/test-plans/work-item/${ticketId}/delete`);
};

// Migration API methods (Platform & Ops - authenticated upload)
export const getMigrationProgress = async () => {
  return authGet('/api/migrations/progress');
};

export const getMigrationLatest = async () => {
  return authGet('/api/migrations/latest');
};

export const getMigrationClientsByWeek = async (week, initiative, segment = 'all', datacenter = '') => {
  const params = { week, initiative, segment };
  if (datacenter) params.datacenter = datacenter;
  return authGet('/api/migrations/list-by-week', params);
};

/**
 * Search migration instances.
 * @param {string} query - instance name / URL substring; empty string returns all.
 * @param {number} limit
 * @param {{ azureMigratedSinceDays?: number }} [opts] - when `azureMigratedSinceDays`
 *   is set, the server restricts results to Azure-migrated instances whose
 *   migration date falls within the last N days and orders them by most-recent
 *   first. Use this for "recently migrated" dropdowns so server-side pagination
 *   doesn't truncate by alphabetical ordering before the date filter runs.
 */
export const getMigrationSearch = async (query, limit = 50, opts = {}) => {
  const params = { q: query, limit };
  if (opts && Number.isFinite(opts.azureMigratedSinceDays) && opts.azureMigratedSinceDays > 0) {
    params.azureMigratedSinceDays = opts.azureMigratedSinceDays;
  }
  return authGet('/api/migrations/search', params);
};

/**
 * Colo vs Azure Datadog metrics for a client row from the latest migration upload.
 * @param {{ q?: string, instanceName?: string, service?: string, days?: number, oldEnv?: string, newEnv?: string }} params
 */
export const getAzureMigrationPerformance = async (params = {}) => {
  return authGet('/api/migrations/azure-migration-performance', params, 'json', {
    useCache: false,
    timeout: 120000
  });
};

export const postMigrationCdnLookup = async (hosts) => {
  return authPost('/api/migrations/cdn-lookup', { hosts });
};

/**
 * Performance snapshot endpoints — MongoDB-backed daily sync.
 * These allow reviewing historical Colo baselines after Datadog's 30-day window expires.
 */

/** Returns the sync job status (lastSyncAt, nextSyncAt, isRunning, progress). */
export const getMigrationPerfSyncStatus = async () => {
  return authGet('/api/migrations/performance-snapshots/sync-status', {}, 'json', { useCache: false });
};

/**
 * Triggers a background Datadog fetch + MongoDB upsert for the given week's clients.
 * Returns 202 immediately; poll getMigrationPerfSyncStatus() to track progress.
 * @param {{ week?: string, instanceNames?: string[] }} [opts]
 */
export const triggerMigrationPerfSync = async (opts = {}) => {
  return authPost('/api/migrations/performance-snapshots/sync', opts);
};

/**
 * Briefing Client Performance Datadog → Mongo monthly rollup sync status.
 * Returns isRunning, progress, lastSyncAt, nextSyncAt (03:00 UTC), skippedClipped, etc.
 */
export const getBriefingPerformanceSyncStatus = async () => {
  return authGet('/api/briefings/performance/sync-status', {}, 'json', { useCache: false });
};

/**
 * The admin-editable Client Performance Datadog filter: { filter, defaults, isDefault, fingerprint, updatedAt }.
 */
export const getBriefingPerformanceFilter = async () => {
  return authGet('/api/briefings/performance/filter', {}, 'json', { useCache: false });
};

/**
 * Saves the filter (admin only). Invalid entries come back as a 400 with the problems in the message.
 * @param {{ excludedActions: string[], extraBotPatterns: string[] }} filter
 */
export const saveBriefingPerformanceFilter = async (filter) => {
  return authPut('/api/briefings/performance/filter', filter);
};

/**
 * Triggers a background Datadog → Mongo sync for briefing Client Performance (admin only).
 * Returns 202 immediately; poll getBriefingPerformanceSyncStatus() to track progress.
 * Uses server Datadog env — do not send API keys from the client.
 * @param {{ instances?: string[] }} [opts]
 */
export const triggerBriefingPerformanceSync = async (opts = {}) => {
  return authPost('/api/briefings/performance/sync', opts);
};

/**
 * Loads the most recent stored snapshot for each client that migrated in `week`.
 * Does NOT hit Datadog — reads from MongoDB only.
 * @param {string} week - ISO date string (YYYY-MM-DD, Monday-anchored week start)
 */
export const getMigrationPerfSnapshots = async (week) => {
  return authGet('/api/migrations/performance-snapshots', { week }, 'json', { useCache: false });
};

export const uploadMigrationExcel = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return authPostFormData('/api/migrations/upload', formData);
};

/** Returns the persisted global resource name exclusion list for Azure Migration error rate. */
export const getAzureMigrationExclusionConfig = async () =>
  authGet('/api/migrations/exclusion-config', {}, 'json', { useCache: false });

/** Replaces the persisted global exclusion list. @param {string[]} resourceNames */
export const updateAzureMigrationExclusionConfig = async (resourceNames) =>
  authPut('/api/migrations/exclusion-config', { resourceNames });

/** PR review / merge metrics for a repo and date range (GitHub API via server). */
export const getMigrationPrReviewMetrics = async (repo, start, end) => {
  return authGet(
    '/api/migrations/pr-review-metrics',
    {
      repo,
      start: start instanceof Date ? start.toISOString() : start,
      end: end instanceof Date ? end.toISOString() : end
    },
    'json',
    { useCache: false }
  );
};

/**
 * GitHub Actions workflow metrics from MongoDB snapshot.
 * @param {{ profile?: 'cutover' | 'provisioning' }} [opts] cutover = HighQ migration workflow; provisioning = himat_provisioning.yml
 */
export const getMigrationGithubWorkflowMetrics = async (opts = {}) => {
  const profile = opts.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authGet('/api/migrations/github-workflow-metrics', { profile }, 'json', { useCache: false });
};

/**
 * Weekly code-change and PR analytics for tr/highq_migration, segmented by target branch.
 * @param {Date|string} start
 * @param {Date|string} end
 */
export const getMigrationCodeChanges = async (start, end) => {
  return authGet(
    '/api/migrations/code-changes',
    {
      start: start instanceof Date ? start.toISOString() : start,
      end: end instanceof Date ? end.toISOString() : end
    },
    'json',
    { useCache: false }
  );
};

/**
 * Fresh sync: clears workflow snapshot for this profile, then pulls from GitHub.
 * @param {{ maxPages?: number, profile?: 'cutover' | 'provisioning' }} [body]
 */
export const postMigrationGithubWorkflowMetricsSync = async (body = {}) => {
  const profile = body.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authPost('/api/migrations/github-workflow-metrics/sync', { ...body, profile }, {
    invalidateCache: `github-workflow-metrics-${profile}`,
    timeout: 900000
  });
};

/**
 * Incremental sync: pages GitHub newest-first, stops at the last known run ID, merges new runs.
 * Requires an existing snapshot (run a full sync first).
 * @param {{ profile?: 'cutover' | 'provisioning' }} [opts]
 */
export const postMigrationGithubWorkflowMetricsSyncIncremental = async (opts = {}) => {
  const profile = opts.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authPost('/api/migrations/github-workflow-metrics/sync-incremental', { profile }, {
    invalidateCache: `github-workflow-metrics-${profile}`,
    timeout: 900000
  });
};

/** Delete cached workflow statistics for this profile from MongoDB (no GitHub pull). */
export const postMigrationGithubWorkflowMetricsClear = async (opts = {}) => {
  const profile = opts.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authPost('/api/migrations/github-workflow-metrics/clear', { profile }, {
    invalidateCache: `github-workflow-metrics-${profile}`
  });
};

/** Re-fetch GitHub workflow attempts for one migration path and update the snapshot (no full DB wipe). */
export const postMigrationGithubWorkflowPathRefresh = async (body) => {
  const profile = body?.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authPost('/api/migrations/github-workflow-metrics/refresh-path', { ...body, profile }, {
    invalidateCache: `github-workflow-metrics-${profile}`,
    timeout: 900000
  });
};

/** Batch refresh: multiple paths in one GitHub list + one DB write (max 200 paths). */
export const postMigrationGithubWorkflowPathsRefresh = async (body) => {
  const profile = body?.profile === 'provisioning' ? 'provisioning' : 'cutover';
  return authPost('/api/migrations/github-workflow-metrics/refresh-paths', { ...body, profile }, {
    invalidateCache: `github-workflow-metrics-${profile}`,
    timeout: 900000
  });
};

// ─── Instance Monitor API (proxied to Python FastAPI on :8000) ───────────────

const IM_BASE = '/api/instance-monitor';

export const getInstanceMonitorHubStatus = async () =>
  get(`${IM_BASE}/hub/env-check`);

export const registerInstanceInMonitor = async (collabUrl, label = '', team = '') =>
  post(`${IM_BASE}/collab/instances`, { collab_url: collabUrl, label, team });

export const getInstanceMonitorMicroservices = async (instanceName) =>
  get(`${IM_BASE}/hub/collab-instance-microservices?name=${encodeURIComponent(instanceName)}`);

export const getInstanceMonitorCbvRaw = async (instanceName, baseUrl, force = false) => {
  const p = new URLSearchParams();
  if (baseUrl) p.set('base_url', baseUrl);
  if (force)   p.set('force', 'true');
  const qs = p.toString() ? `?${p}` : '';
  return get(`${IM_BASE}/collab/instances/${encodeURIComponent(instanceName)}/getcbv-raw${qs}`, null, 'text');
};

export const getInstanceMonitorMsvRaw = async (instanceName, baseUrl, force = false) => {
  const p = new URLSearchParams();
  if (baseUrl) p.set('base_url', baseUrl);
  if (force)   p.set('force', 'true');
  const qs = p.toString() ? `?${p}` : '';
  return get(`${IM_BASE}/collab/instances/${encodeURIComponent(instanceName)}/getmsv-raw${qs}`, null, 'text');
};

export const getInstanceMonitorAzureRaw = async (instanceName, baseUrl, force = false) => {
  const p = new URLSearchParams();
  if (baseUrl) p.set('base_url', baseUrl);
  if (force)   p.set('force', 'true');
  const qs = p.toString() ? `?${p}` : '';
  return get(`${IM_BASE}/collab/instances/${encodeURIComponent(instanceName)}/getazure-raw${qs}`, null, 'text');
};

// Default export for convenience - must be at the end after all functions are defined
const apiServiceDefaultExport = {
  get,
  authGet,
  authPost,
  authPostFormData,
  authPut,
  authPatch,
  authDelete,
  post,
  deleteRequest,
  getComments,
  addComment,
  deleteComment,
  getWSRItems,
  getWSRCurrentQuarterItems,
  addWSRItem,
  removeWSRItem,
  moveToWeeklyReport,
  removeFromWeeklyReport,
  updateWSRItem,
  getWSRItemDetails,
  getWSRItemBugs,
  getWSRItemTestCases,
  refreshWSRItem,
  refreshWSRItems,
  getWSRRefreshStatus,
  autoPopulateWSRItems,
  generateWSRReport,
  listWSRReports,
  getWSRReportLog,
  getPlanningItems,
  setPlanningQuarter,
  resetPlanningToTargetDate,
  getTeamsWithStreams,
  getWorkItemHierarchy,
  getDashboardMetrics,
  getCompletionMetrics,
  getQualityMetricWorkItems,
  getEpics,
  getFeatures,
  getFixVersions,
  getAllFixVersions,
  getFixVersion,
  createFixVersion,
  updateFixVersion,
  deleteFixVersion,
  generateReleaseNotes,
  deleteReleaseNotes,
  getWorkItems,
  getProductionBugs,
  getInternalBugs,
  getTsiTickets,
  getTsiTicketStats,
  getTsiTicketColumns,
  clearTsiTickets,
  getTsiOpenVsClosedChart,
  getTsiClosedByClassificationChart,
  getTsiWeeklyIncomingByPriorityChart,
  getTsiNonClosedByPriorityChart,
  getTsiDailyCreatedVsClosedChart,
  getTsiAssignees,
  getTsiAverageAgeByCreatedMonth,
  getTsiAverageAgeByClosedMonth,
  getTsiWeeklyIncomingByEnvironmentChart,
  getTsiWeeklyIncomingByClassificationChart,
  uploadTsiTicketsCsv,
  getTsiTicketsByClassificationAndMonth,
  getTsiTicketsByAssigneeAndMonth,
  getTsiAssigneePivotTable,
  getLinkedBugsForTsiTickets,
  getTsiTicketLinks,
  getBugCycleTimeMetrics,
  getPRCodeChanges,
  // Drill4J API methods
  syncDrill4JTests,
  getDrill4JTests,
  getDrill4JTestsByDiscrepancyType,
  getTestsNotInDrill4J,
  // Coverage (Drill4J tree) API methods
  getCoverageBuilds,
  getCoverageOverview,
  getCoveragePackages,
  getCoverageClasses,
  getCoverageMethods,
  getCoverageTestsForMethod,
  searchCoverage,
  getCoverageModuleStructure,
  // ServiceNow API methods
  getServiceNowConfig,
  updateServiceNowConfig,
  testServiceNowConnection,
  getServiceNowIncidents,
  getServiceNowTickets,
  downloadServiceNowIncidentReport,
  syncServiceNowIncidents,
  getServiceNowTicketStats,
  // Test Instances API methods
  getTestInstances,
  getTestInstanceById,
  createTestInstance,
  updateTestInstance,
  deleteTestInstance,
  fetchTestInstanceCBV,
  fetchTestInstanceMSV,
  fetchTestInstanceAzure,
  getInstanceManagementNamespaces,
  createInstanceManagementNamespace,
  updateInstanceManagementNamespace,
  deleteInstanceManagementNamespace,
  createInstanceManagementService,
  updateInstanceManagementService,
  deleteInstanceManagementService,
  fetchInstanceManagementServiceCBV,
  exportInstanceManagementData,
  importInstanceManagementData,
  getTestPlanLogs,
  // Test Scenarios API methods
  getScenarios,
  getScenario,
  markScenarioIrrelevant,
  markScenarioRelevant,
  createAdoTestCase,
  linkAdoTestCase,
  unlinkAdoTestCase,
  getTestCaseFields,
  // Test Cases API methods
  // refineTestCases, // DISABLED for offline work
  getTestCases,
  getTestCase,
  createAdoTestCaseForTestCase,
  linkAdoTestCaseForTestCase,
  unlinkAdoTestCaseForTestCase,
  deleteTestPlan,
  getActiveSessionsCount,
  getActiveSessions,
  getActivityStats,
  // Migration API methods
  getMigrationProgress,
  getMigrationLatest,
  getMigrationClientsByWeek,
  getMigrationSearch,
  getAzureMigrationPerformance,
  getMigrationPerfSyncStatus,
  triggerMigrationPerfSync,
  getBriefingPerformanceSyncStatus,
  getBriefingPerformanceFilter,
  saveBriefingPerformanceFilter,
  triggerBriefingPerformanceSync,
  getMigrationPerfSnapshots,
  getAzureMigrationExclusionConfig,
  updateAzureMigrationExclusionConfig,
  postMigrationCdnLookup,
  uploadMigrationExcel,
  getMigrationPrReviewMetrics,
  getMigrationGithubWorkflowMetrics,
  getMigrationCodeChanges,
  postMigrationGithubWorkflowMetricsSync,
  postMigrationGithubWorkflowMetricsClear,
  postMigrationGithubWorkflowPathRefresh,
  postMigrationGithubWorkflowPathsRefresh,
  // Instance Monitor API methods (proxied to Python FastAPI on :8000)
  getInstanceMonitorHubStatus,
  registerInstanceInMonitor,
  getInstanceMonitorMicroservices,
  getInstanceMonitorCbvRaw,
  getInstanceMonitorMsvRaw,
};

export default apiServiceDefaultExport;

// ── Health Check ─────────────────────────────────────────────────────────────

export const startHealthCheckRun = async (filter = null) =>
  authPost('/api/health-check/start', { filter });

export const stopHealthCheckRun = async () =>
  authPost('/api/health-check/stop', {});

export const pauseHealthCheckRun = async () =>
  authPost('/api/health-check/pause', {});

export const resumeHealthCheckRun = async () =>
  authPost('/api/health-check/resume', {});

export const getHealthCheckStatus = async () =>
  get('/api/health-check/status');

export const getHealthCheckResults = async (runId) =>
  get(`/api/health-check/results/${runId}`);

export const getHealthCheckRuns = async (limit = 20) =>
  get('/api/health-check/runs', { limit });

export const rerunHealthCheckFailed = async (runId) =>
  authPost(`/api/health-check/rerun/${runId}`, {});

export const clearHealthCheckRuns = async () => {
  const baseUrl = await getCurrentBaseUrl();
  const token = localStorage.getItem('token');
  const res = await fetch(`${baseUrl}/api/health-check/runs`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Clear history failed: ${res.status}`);
  return res.json();
};
